import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Brush, Eraser, Menu, PaintBucket, Pipette, X } from "lucide-react";
import {
  BACKGROUND_PIECES,
  CENTER_PIECES,
  DEFAULT_BASIC_PIECES,
  FOREGROUND_PIECES,
  GAMEPLAY_TYPES,
  LAYER,
  LAYER_ORDER,
  getPaletteForLayer,
  getPieceSize,
  normalizePieceType
} from "./pieceCatalog";
import {
  getMalikSummonRules,
  getPieceMinSharedThreatQuantity,
  getPieceDisplayName,
  getPieceHitPoints,
  getPieceImage,
  isMalikPalettePiece,
  usesSharedThreatQuantity
} from "./pieceMetadata";
import {
  buildSingleLevelExport,
  createBoard,
  getPieceIndex,
  normalizeLevel,
  resizeBoard,
  sanitizeCsvPieces
} from "./levelSchema";
import {
  changeOwnWebBuilderPassword,
  createWebBuilderLevel,
  deleteWebBuilderLevel,
  getAdminWebBuilderLevel,
  getWebBuilderLevel,
  listAdminWebBuilderLevels,
  listWebBuilderLevels,
  updateWebBuilderLevel
} from "./webBuilderApi";
import { isProductionEnvironment } from "./environmentConfig";

const TOOL = {
  PAINT: "paint",
  ERASE: "erase",
  PICK: "pick",
  FILL: "fill"
};

const TOOL_OPTIONS = [
  { mode: TOOL.PAINT, label: "Paint", Icon: Brush },
  { mode: TOOL.ERASE, label: "Erase", Icon: Eraser },
  { mode: TOOL.PICK, label: "Pick", Icon: Pipette },
  { mode: TOOL.FILL, label: "Fill", Icon: PaintBucket }
];

const PALETTE_CONTENT = {
  MALIK: "malik",
  BOARD: "board"
};

const MISSING_SPRITE_FALLBACKS = {
  FireTile_0: "HauntedTile_0",
  FireTile_1: "HauntedTile_0",
  FireTile_2: "HauntedTile_0",
  LightSpark: "SmartCannon",
  Spark: "SmartCannon",
  MegaSpark: "SmartCannon",
  LightningWish: "MulticolorBlue",
  LightningStrike: "MulticolorBlue",
  LightningStorm: "MulticolorBlue",
  TopBoomerang: "TopRune",
  BottomBoomerang: "BottomRune",
  LeftBoomerang: "LeftRune",
  RightBoomerang: "RightRune",
  TopRightBoomerang: "TopRightRune",
  TopLeftBoomerang: "TopLeftRune",
  BottomRightBoomerang: "BottomRightRune",
  BottomLeftBoomerang: "BottomLeftRune",
  Cinders: "LavaBall_0",
  FireWish: "LavaBall_0",
  Flare: "LavaBall_1"
};

const LAYER_FALLBACK_SPRITE = {
  [LAYER.BACKGROUND]: "Grass_0",
  [LAYER.CENTER]: "NoElement",
  [LAYER.FOREGROUND]: "SpiderWeb_0"
};

const MALIK_LAYOUT = {
  COLUMNS: 9,
  ROWS: 13,
  NO_ELEMENT_ROWS: 5,
  GAP_ROW: 5,
  BOTTOM_START_ROW: 6
};
const BOARD_CELL_SIZE_PX = 62;
const BOARD_CELL_GAP_PX = 2;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 2;
const MOBILE_LAYOUT_BREAKPOINT = 1024;
const MIN_LEFT_PANEL_WIDTH = 260;
const MAX_LEFT_PANEL_WIDTH = 620;
const MIN_PALETTE_WIDTH = 360;
const MAX_PALETTE_WIDTH = 680;
const THREAT_BASE_TYPES = new Set([
  "WoodenBox",
  "BloodBag",
  "Grass",
  "SteelBox",
  "GoldBag",
  "Bookcase",
  "Honey",
  "TreasureMap",
  "SpiderWeb",
  "Horse",
  "WateringCan",
  "Turtle",
  "CloverGarden",
  "Flower",
  "FireTile",
  "HauntedTile",
  "ShipAnchor",
  "AlchemyBox",
  "CursedBookSquare",
  "FishNet",
  "Worm",
  "Dolphin",
  "Chains",
  "LavaBoulder",
  "LavaBall",
  "Volcano",
  "MagicGemCore",
  "DarkClouds",
  "ElectricTower",
  "StaticField",
  "Spider",
  "WoodTotem",
  "Sphynx",
  "ChargeBattery",
  "MagicalFruit",
  "SarcophagusSquare",
  "CursedBookLT",
  "CursedBookFour",
  "CursedBookFive",
  "Tombstone",
  "SarcophagusLT",
  "SarcophagusFour",
  "SarcophagusFive",
  "Blob",
  "Ice",
  "IceMetal",
  "Shelf",
  "Malik_Ghost",
  "Malik_Bone_Golem",
  "Malik_Skeleton",
  "Malik_Skull_Slime",
  "Malik_Skull_Big_Slime",
  "Malik_Bone_Naga",
  "Malik_Zombie",
  "Malik_Snake",
  "Malik_Necrotic_Egg",
  "Malik_Wall_One",
  "Malik_Wall_Four",
  "Malik_Wall_Nine",
  "Malik_Wall_One_Silver",
  "Malik_Skull_Slime_Weak",
  "Malik_Skull_Big_Slime_Weak"
]);
const SPAWNER_THREAT_BASE_TYPES = new Set([
  "Spawner",
  "TreasureMap",
  "WateringCan",
  "FishNet",
  "Volcano",
  "ElectricTower",
  "Spider"
]);
const SPAWNER_TARGET_BY_TYPE = {
  CloverGarden: "Grass",
  Flower: "Grass",
  WateringCan: "Grass",
  TreasureMap: "TreasureMap",
  FishNet: "Horse",
  Volcano: "LavaBall",
  ElectricTower: "ElectricTower",
  Spider: "SpiderWeb"
};
const BACKGROUND_TARGET_BASE_TYPES = new Set(["Grass", "FireTile", "HauntedTile", "StaticField"]);
const FOREGROUND_TARGET_BASE_TYPES = new Set(["Honey", "SpiderWeb", "Chains", "DarkClouds"]);
const ALCHEMY_COLOR_OPTIONS = ["Piece_Blue", "Piece_Yellow", "Piece_Red", "Piece_Green"];
const DEFAULT_ALCHEMY_COLOR = "Piece_Blue";
const ALCHEMY_FAMILY_BY_COLOR = Object.freeze({
  Piece_Blue: "Breaker",
  Piece_Yellow: "Summon",
  Piece_Red: "Fire",
  Piece_Green: "Trickster"
});
const ALCHEMY_COLOR_BY_FAMILY = Object.freeze({
  Breaker: "Piece_Blue",
  Summon: "Piece_Yellow",
  Fire: "Piece_Red",
  Trickster: "Piece_Green"
});
const LEGACY_ALCHEMY_STAGE_TYPES = new Set(["AlchemyBox_0", "AlchemyBox_1", "AlchemyBox_2", "AlchemyBox_3"]);
const HIDDEN_WORM_PROXY_TYPE = "Worm_Hidden";
const WORM_TYPES = new Set(["Worm", "Worm_0", "Worm_1"]);
const KNOWN_VARIANTS_BY_BASE = (() => {
  const byBase = new Map();
  const catalogPieces = [...BACKGROUND_PIECES, ...CENTER_PIECES, ...FOREGROUND_PIECES];

  catalogPieces.forEach((pieceType) => {
    if (typeof pieceType !== "string") return;
    const subtypeMatch = pieceType.match(/^(.*)_\d+$/);
    if (!subtypeMatch) return;

    const baseType = subtypeMatch[1];
    if (!byBase.has(baseType)) {
      byBase.set(baseType, []);
    }

    const variants = byBase.get(baseType);
    if (!variants.includes(pieceType)) {
      variants.push(pieceType);
    }
  });

  return byBase;
})();

function clamp(value, minValue, maxValue) {
  return Math.min(maxValue, Math.max(minValue, value));
}

function normalizeOptionalText(value) {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized || null;
}

function getCreatorDisplayName(levelEntry) {
  return (
    normalizeOptionalText(levelEntry?.creator_name) ??
    normalizeOptionalText(levelEntry?.creator_display_name) ??
    normalizeOptionalText(levelEntry?.creator_username) ??
    "unknown"
  );
}

function isMalikEnemyPiece(type) {
  return isMalikPalettePiece(type);
}

function isWormType(type) {
  return WORM_TYPES.has(type);
}

function isMalikTopCenterPieceAllowed(type) {
  return type === "Empty" || type === "NoElement" || isMalikEnemyPiece(type);
}

function createThreatClearBoardTemplate(collumn = 9, line = 9) {
  const board = createBoard(collumn, line);
  board.pieces.forEach((piece) => {
    piece.type = "RandomElement";
    piece.parent = { x: piece.line, y: piece.collumn };
  });
  return board;
}

function createFreshLevel(id) {
  return normalizeLevel(
    {
      id,
      maxMove: 30,
      initialWishProgressValue: 0,
      gameplayType: "MalikFight",
      basicPieces: [...DEFAULT_BASIC_PIECES],
      board: createMalikBoardTemplate(),
      malikBoardPhases: []
    },
    0
  );
}

function isMalikFightLevel(level) {
  return level?.gameplayType === "MalikFight";
}

function isMalikBoardFixed(board) {
  return board?.collumn === MALIK_LAYOUT.COLUMNS && board?.line === MALIK_LAYOUT.ROWS;
}

function createMalikBoardTemplate() {
  const board = createBoard(MALIK_LAYOUT.COLUMNS, MALIK_LAYOUT.ROWS);

  board.pieces.forEach((piece) => {
    if (piece.line < MALIK_LAYOUT.NO_ELEMENT_ROWS) {
      piece.type = "NoElement";
    } else if (piece.line === MALIK_LAYOUT.GAP_ROW) {
      piece.type = "Empty";
    } else if (piece.line === MALIK_LAYOUT.ROWS - 1) {
      piece.type = "Spawner";
    } else {
      piece.type = "RandomElement";
    }

    piece.backgroundType = "Empty";
    piece.foregroundType = "Empty";
    piece.parent = { x: piece.line, y: piece.collumn };
  });

  return board;
}

function getLayerField(layer) {
  if (layer === LAYER.BACKGROUND) return "backgroundType";
  if (layer === LAYER.FOREGROUND) return "foregroundType";
  return "type";
}

function pieceLabel(type) {
  if (!type || type === "Empty") return "";
  return getPieceDisplayName(type);
}

function getMalikSummonRulesForType(type) {
  const normalizedType = normalizeTargetTypeName(type);
  return getMalikSummonRules(normalizedType);
}

function formatMalikSummonRule(rule) {
  if (!rule) return "";
  const spawnLabel = pieceLabel(rule.spawnType) || rule.spawnType || "Unknown";
  const detail = typeof rule.description === "string" ? rule.description.trim() : "";
  const cooldownTurns = Number.isFinite(Number(rule?.cooldownTurns))
    ? Math.max(1, Number(rule.cooldownTurns))
    : null;
  const trigger = typeof rule?.trigger === "string" ? rule.trigger : "";
  const showCooldown = trigger !== "death" && cooldownTurns != null;
  const cooldownSuffix = showCooldown ? ` (CD ${cooldownTurns})` : "";
  return detail ? `${spawnLabel}: ${detail}${cooldownSuffix}` : `${spawnLabel}${cooldownSuffix}`;
}

function buildPieceSummonTooltip(type) {
  const summonRules = getMalikSummonRulesForType(type);
  if (!summonRules.length) return "";

  return [
    "Summons:",
    ...summonRules.map((rule) => `- ${formatMalikSummonRule(rule)}`)
  ].join("\n");
}

function buildPieceHoverTooltip(type, extras = []) {
  const lines = [pieceLabel(type) || type];
  const summonText = buildPieceSummonTooltip(type);
  if (summonText) {
    lines.push(summonText);
  }

  extras.filter(Boolean).forEach((line) => lines.push(line));
  return lines.join("\n");
}

function buildTargetCardTooltip(target) {
  if (!target) {
    return "";
  }

  const lines = [pieceLabel(target.type) || target.type];

  if (target.isSpawned) {
    lines.push("Spawned target");
    if (Array.isArray(target.summonSources) && target.summonSources.length > 0) {
      lines.push("Sources:");
      target.summonSources.forEach((source) => {
        lines.push(`- ${pieceLabel(source.sourceType) || source.sourceType} x${source.count}`);
      });
    }
    if (Array.isArray(target.summonDetails) && target.summonDetails.length > 0) {
      lines.push("Rules:");
      target.summonDetails.forEach((detail) => lines.push(`- ${detail}`));
    }
  } else {
    const summonText = buildPieceSummonTooltip(target.type);
    if (summonText) {
      lines.push(summonText);
    }
  }

  return lines.join("\n");
}

function getPieceSpawnStats(type) {
  const summonRules = getMalikSummonRulesForType(type);
  if (!summonRules.length) {
    return { totalCount: 0, iconType: "Empty", details: [], cooldownLabel: "" };
  }

  let totalCount = 0;
  const countByType = new Map();
  const cooldownTurns = [];

  summonRules.forEach((rule) => {
    const contribution = Number.isFinite(Number(rule?.contribution))
      ? Math.max(1, Number(rule.contribution))
      : 1;
    totalCount += contribution;

    const spawnType = normalizeTargetTypeName(rule?.spawnType);
    if (!spawnType || spawnType === "Empty" || spawnType === "NoElement") {
      return;
    }
    countByType.set(spawnType, (countByType.get(spawnType) ?? 0) + contribution);

    const ruleTrigger = typeof rule?.trigger === "string" ? rule.trigger : "";
    const ruleCooldown = Number.isFinite(Number(rule?.cooldownTurns))
      ? Math.max(1, Number(rule.cooldownTurns))
      : null;
    if (ruleTrigger !== "death" && ruleCooldown != null) {
      cooldownTurns.push(ruleCooldown);
    }
  });

  const iconType =
    [...countByType.entries()].sort((left, right) => right[1] - left[1])[0]?.[0] ??
    normalizeTargetTypeName(summonRules[0]?.spawnType) ??
    "Empty";

  const uniqueCooldownTurns = [...new Set(cooldownTurns)];
  const cooldownLabel =
    uniqueCooldownTurns.length === 0
      ? ""
      : uniqueCooldownTurns.length === 1
        ? String(uniqueCooldownTurns[0])
        : uniqueCooldownTurns.join("/");

  return {
    totalCount,
    iconType,
    details: summonRules.map((rule) => formatMalikSummonRule(rule)).filter(Boolean),
    cooldownLabel
  };
}

function isMalikSeparatorRow(line) {
  return line === MALIK_LAYOUT.GAP_ROW;
}

function pieceHitPoints(type) {
  if (!type || type === "Empty") return null;
  const hp = getPieceHitPoints(type);
  return Number.isFinite(hp) && hp > 0 ? hp : null;
}

function pieceHitPointsForBoardPiece(piece) {
  const pieceType = piece?.type;
  if (!pieceType || pieceType === "Empty" || pieceType === "PartOf") {
    return null;
  }

  let hp = pieceHitPoints(pieceType);
  if (!Number.isFinite(hp) || hp <= 0) {
    return null;
  }

  if (piece?.overrideMalikThreatLife) {
    const overrideHp = Number.parseInt(piece.malikThreatLifeOverride, 10);
    if (Number.isFinite(overrideHp) && overrideHp > 0) {
      hp = overrideHp;
    }
  }

  return hp;
}

function uniqueTypes(values) {
  return [...new Set(values.filter(Boolean))];
}

function normalizeTargetTypeName(type) {
  if (typeof type !== "string") return "Empty";
  const trimmed = type.trim();
  if (!trimmed) return "Empty";

  const subtypeMatch = trimmed.match(/^(.*)_\d+$/);
  if (!subtypeMatch) return trimmed;

  const baseType = subtypeMatch[1];
  if (THREAT_BASE_TYPES.has(baseType) || SPAWNER_THREAT_BASE_TYPES.has(baseType)) {
    return baseType;
  }

  return trimmed;
}

function normalizeBaseTypeForTargets(type) {
  if (type === "RandomElement" || type === "StartRandomElement") {
    return "RandomElement";
  }
  return normalizeTargetTypeName(type);
}

function isAlchemyBoxType(type) {
  return normalizeTargetTypeName(type) === "AlchemyBox";
}

function parseAlchemyPaletteType(type) {
  const match = typeof type === "string" ? type.match(/^AlchemyBox_(Breaker|Summon|Fire|Trickster)_([0-3])$/) : null;
  if (!match) {
    return null;
  }

  return {
    family: match[1],
    stage: Number.parseInt(match[2], 10) || 0,
    pieceColor: ALCHEMY_COLOR_BY_FAMILY[match[1]] ?? DEFAULT_ALCHEMY_COLOR
  };
}

function normalizeAlchemyColor(value) {
  return ALCHEMY_COLOR_OPTIONS.includes(value) ? value : DEFAULT_ALCHEMY_COLOR;
}

function getAlchemyStage(type) {
  if (type === "AlchemyBox") {
    return 3;
  }

  const match = typeof type === "string" ? type.match(/^AlchemyBox_(\d+)$/) : null;
  if (!match) return 0;

  return clamp(Number.parseInt(match[1], 10) || 0, 0, 3);
}

function getAlchemySpriteType(type, pieceColor) {
  const family = ALCHEMY_FAMILY_BY_COLOR[normalizeAlchemyColor(pieceColor)] ?? "Breaker";
  const stage = getAlchemyStage(type);
  return `AlchemyBox_${family}_${clamp(stage, 0, 3)}`;
}

function isThreatType(type) {
  return THREAT_BASE_TYPES.has(normalizeTargetTypeName(type));
}

function isSpawnerThreatType(type) {
  return SPAWNER_THREAT_BASE_TYPES.has(normalizeTargetTypeName(type));
}

function addOrAccumulateTarget(targets, type, quantity) {
  if (!type || type === "Empty") return;

  if (quantity === 0) {
    if (!targets.has(type)) {
      targets.set(type, 0);
    }
    return;
  }

  targets.set(type, (targets.get(type) ?? 0) + quantity);
}

function accumulateLayerTargets(targets, rawType, piece, { isBackground = false, isForeground = false } = {}) {
  const normalizedType = normalizeBaseTypeForTargets(rawType);

  if (!isBackground && !isForeground && normalizedType === "Spawner") {
    const sequentialPieces = piece?.spawnerData?.sequentialPieces;
    if (Array.isArray(sequentialPieces)) {
      sequentialPieces.forEach((sequentialType) => {
        const normalizedSequentialType = normalizeTargetTypeName(sequentialType);
        if (isThreatType(normalizedSequentialType)) {
          addOrAccumulateTarget(targets, normalizedSequentialType, 1);
        }
      });
    }
    return;
  }

  if (!isThreatType(normalizedType)) return;

  let quantity = 1;
  if (usesSharedThreatQuantity(normalizedType)) {
    quantity = Math.max(0, Number(piece?.spawnerData?.quantity) || 0);
  } else if (isSpawnerThreatType(normalizedType) && piece?.spawnerData) {
    quantity = Math.max(0, Number(piece.spawnerData.quantity) || 0);
  }

  let displayType = normalizedType;
  if (isSpawnerThreatType(normalizedType)) {
    const mappedTarget = normalizeTargetTypeName(SPAWNER_TARGET_BY_TYPE[normalizedType] ?? "Empty");
    if (mappedTarget !== "Empty") {
      displayType = mappedTarget;
    }
  }

  addOrAccumulateTarget(targets, displayType, quantity);
  addOrAccumulateTarget(targets, displayType, 0);
}

function buildThreatTargets(board) {
  if (!board?.pieces) return [];

  const targets = new Map();
  board.pieces.forEach((piece) => {
    accumulateLayerTargets(targets, piece.type, piece);

    if (piece.backgroundType !== "Empty") {
      accumulateLayerTargets(targets, piece.backgroundType, piece, { isBackground: true });
    }

    if (piece.foregroundType !== "Empty") {
      accumulateLayerTargets(targets, piece.foregroundType, piece, { isForeground: true });
    }
  });

  return [...targets.entries()]
    .map(([type, quantity]) => ({ type, quantity }))
    .sort((left, right) => {
      if (right.quantity !== left.quantity) return right.quantity - left.quantity;
      return pieceLabel(left.type).localeCompare(pieceLabel(right.type));
    });
}

function getSharedThreatPieces(board, pieceType) {
  if (!board?.pieces) return [];
  const normalizedType = normalizeTargetTypeName(pieceType);
  return board.pieces.filter((piece) => normalizeTargetTypeName(piece?.type) === normalizedType);
}

function collectSharedThreatTotals(board) {
  const totals = new Map();
  if (!board?.pieces) {
    return totals;
  }

  board.pieces.forEach((piece) => {
    const normalizedType = normalizeTargetTypeName(piece?.type);
    if (!usesSharedThreatQuantity(normalizedType) || totals.has(normalizedType)) {
      return;
    }

    const sharedQuantity = getSharedThreatQuantity(board, normalizedType);
    if (sharedQuantity != null) {
      totals.set(normalizedType, sharedQuantity);
    }
  });

  return totals;
}

function getSharedThreatQuantity(board, pieceType) {
  const matchingPieces = getSharedThreatPieces(board, pieceType);
  if (matchingPieces.length === 0) {
    return null;
  }

  const normalizedType = normalizeTargetTypeName(pieceType);
  const minimum = getPieceMinSharedThreatQuantity(normalizedType);

  const total = matchingPieces.reduce(
    (runningTotal, piece) => runningTotal + Math.max(0, Number(piece?.spawnerData?.quantity) || 0),
    0
  );

  return Math.max(minimum, total);
}

function syncSharedThreatQuantity(board, pieceType, quantity) {
  const normalizedType = normalizeTargetTypeName(pieceType);
  const minimum = getPieceMinSharedThreatQuantity(normalizedType);
  const nextValue = Math.max(minimum, Number.parseInt(quantity, 10) || 0);
  const matchingPieces = getSharedThreatPieces(board, normalizedType);

  matchingPieces.forEach((piece) => {
    piece.spawnerData.quantity = 0;
  });

  if (matchingPieces.length === 0) {
    return nextValue;
  }

  if (nextValue >= matchingPieces.length) {
    matchingPieces[0].spawnerData.quantity = nextValue - (matchingPieces.length - 1);
    for (let index = 1; index < matchingPieces.length; index += 1) {
      matchingPieces[index].spawnerData.quantity = 1;
    }
    return nextValue;
  }

  let remaining = nextValue;
  matchingPieces.forEach((piece) => {
    if (remaining > 0) {
      piece.spawnerData.quantity = 1;
      remaining -= 1;
    }
  });

  return nextValue;
}

function syncBoardSharedThreatQuantities(board, preferredTotals = null) {
  if (!board?.pieces) return;

  const sharedTypes = new Set(preferredTotals ? [...preferredTotals.keys()] : []);
  board.pieces.forEach((piece) => {
    const normalizedType = normalizeTargetTypeName(piece?.type);
    if (usesSharedThreatQuantity(normalizedType)) {
      sharedTypes.add(normalizedType);
    }
  });

  sharedTypes.forEach((pieceType) => {
    const matchingPieces = getSharedThreatPieces(board, pieceType);
    if (matchingPieces.length === 0) {
      return;
    }

    const preferredQuantity = preferredTotals?.get(pieceType);
    const sharedQuantity =
      preferredQuantity ?? getSharedThreatQuantity(board, pieceType) ?? getPieceMinSharedThreatQuantity(pieceType);
    if (sharedQuantity != null) {
      syncSharedThreatQuantity(board, pieceType, sharedQuantity);
    }
  });
}

function buildMalikTargetSummary(pieces) {
  if (!Array.isArray(pieces)) {
    return { targets: [], spawnedTargets: [], totalHp: 0 };
  }

  const targets = new Map();
  const spawnedTargets = new Map();
  let totalHp = 0;

  pieces.forEach((piece) => {
    const normalizedType = normalizePieceType(piece?.type, "Empty");
    if (
      normalizedType === "Empty" ||
      normalizedType === "NoElement" ||
      normalizedType === "PartOf"
    ) {
      return;
    }

    if (!isMalikEnemyPiece(normalizedType)) {
      return;
    }

    targets.set(normalizedType, (targets.get(normalizedType) ?? 0) + 1);

    const summonRules = getMalikSummonRulesForType(normalizedType);
    summonRules.forEach((rule) => {
      const spawnedType = normalizeTargetTypeName(rule.spawnType);
      if (!spawnedType || spawnedType === "Empty" || spawnedType === "NoElement") {
        return;
      }

      const entry =
        spawnedTargets.get(spawnedType) ??
        {
          type: spawnedType,
          quantity: 0,
          isSpawned: true,
          sources: new Map(),
          details: new Set()
        };
      const contribution = Number.isFinite(Number(rule.contribution))
        ? Math.max(1, Number(rule.contribution))
        : 1;
      entry.quantity += contribution;
      entry.sources.set(normalizedType, (entry.sources.get(normalizedType) ?? 0) + 1);
      const formattedRule = formatMalikSummonRule(rule);
      if (formattedRule) {
        entry.details.add(formattedRule);
      }
      spawnedTargets.set(spawnedType, entry);
    });

    const hp = pieceHitPointsForBoardPiece(piece);
    if (Number.isFinite(hp) && hp > 0) {
      totalHp += hp;
    }
  });

  const baseTargets = [...targets.entries()]
    .map(([type, quantity]) => ({ type, quantity, isSpawned: false }))
    .sort((left, right) => {
      if (right.quantity !== left.quantity) return right.quantity - left.quantity;
      return pieceLabel(left.type).localeCompare(pieceLabel(right.type));
    });

  const summonTargets = [...spawnedTargets.values()]
    .map((entry) => ({
      type: entry.type,
      quantity: entry.quantity,
      isSpawned: true,
      summonSources: [...entry.sources.entries()]
        .map(([sourceType, count]) => ({ sourceType, count }))
        .sort((left, right) => {
          if (right.count !== left.count) return right.count - left.count;
          return pieceLabel(left.sourceType).localeCompare(pieceLabel(right.sourceType));
        }),
      summonDetails: [...entry.details]
    }))
    .sort((left, right) => {
      if (right.quantity !== left.quantity) return right.quantity - left.quantity;
      return pieceLabel(left.type).localeCompare(pieceLabel(right.type));
    });

  return {
    targets: baseTargets,
    spawnedTargets: summonTargets,
    totalHp
  };
}

function getTargetLayer(type) {
  const baseType = normalizeTargetTypeName(type);
  if (FOREGROUND_TARGET_BASE_TYPES.has(baseType)) return LAYER.FOREGROUND;
  if (BACKGROUND_TARGET_BASE_TYPES.has(baseType)) return LAYER.BACKGROUND;
  return LAYER.CENTER;
}

function buildAnchorFootprintMap(board) {
  const footprints = new Map();
  if (!board?.pieces) return footprints;

  board.pieces.forEach((piece) => {
    if (piece.type === "PartOf") return;
    const size = getPieceSize(piece.type);
    if (size.width <= 1 && size.height <= 1) return;
    footprints.set(`${piece.collumn}:${piece.line}`, size);
  });

  board.pieces.forEach((piece) => {
    if (piece.type !== "PartOf") return;

    const anchorCollumn = piece.parent?.y;
    const anchorLine = piece.parent?.x;
    if (!Number.isInteger(anchorCollumn) || !Number.isInteger(anchorLine)) return;

    if (
      anchorCollumn < 0 ||
      anchorLine < 0 ||
      anchorCollumn >= board.collumn ||
      anchorLine >= board.line
    ) {
      return;
    }

    const key = `${anchorCollumn}:${anchorLine}`;
    const existing = footprints.get(key) ?? { width: 1, height: 1 };
    footprints.set(key, {
      width: Math.max(existing.width, piece.collumn - anchorCollumn + 1),
      height: Math.max(existing.height, piece.line - anchorLine + 1)
    });
  });

  return footprints;
}

function buildSpriteCandidates(type, layer, allowEmpty) {
  if (!type) return [];
  if (type === "Empty" && !allowEmpty) return [];

  const configuredImage = getPieceImage(type);
  const candidates = configuredImage ? [configuredImage, type] : [type];
  const mappedFallback = MISSING_SPRITE_FALLBACKS[type];
  if (mappedFallback) candidates.push(mappedFallback);

  if (type.includes("_")) {
    const baseType = type.split("_")[0];
    candidates.push(baseType);
    const knownVariants = KNOWN_VARIANTS_BY_BASE.get(baseType) ?? [];
    knownVariants.forEach((variant) => candidates.push(variant));
    candidates.push(`${baseType}_0`);
  } else {
    const knownVariants = KNOWN_VARIANTS_BY_BASE.get(type) ?? [];
    knownVariants.forEach((variant) => candidates.push(variant));
    candidates.push(`${type}_0`);
  }

  if (type === "PartOf") {
    candidates.push("NoElement");
  }

  candidates.push(LAYER_FALLBACK_SPRITE[layer]);
  candidates.push("NoElement");
  return uniqueTypes(candidates);
}

function PieceSprite({ type, layer, className, allowEmpty = false, style, children }) {
  const candidates = useMemo(
    () => buildSpriteCandidates(type, layer, allowEmpty),
    [type, layer, allowEmpty]
  );
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [candidates]);

  const activeType = candidates[candidateIndex];
  const spriteSrc = activeType ? `${import.meta.env.BASE_URL}sprites/${activeType}.png` : "";
  const missingSprite = !spriteSrc;
  const showTextFallback = missingSprite && type !== "Empty";

  return (
    <span className={`sprite-layer ${className || ""}`.trim()} style={style}>
      {spriteSrc && (
        <img
          src={spriteSrc}
          alt={pieceLabel(type) || type || "Tile"}
          draggable={false}
          onError={() => {
            setCandidateIndex((previous) => previous + 1);
          }}
        />
      )}
      {showTextFallback && (
        <span className="sprite-text-fallback">{pieceLabel(type) || type || "Empty"}</span>
      )}
      {children}
    </span>
  );
}

function parsePieceCsvAllowEmpty(value) {
  if (!value || typeof value !== "string") return [];

  const unique = [];
  const seen = new Set();
  value
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const normalized = normalizePieceType(token, "");
      if (!normalized) return;
      if (seen.has(normalized)) return;
      seen.add(normalized);
      unique.push(normalized);
    });

  return unique;
}

const DEFAULT_AUTO_LEVEL_PREFIX = "BUILDER-LEVEL";

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getAutoLevelPrefixForUsername(username) {
  if (typeof username !== "string") {
    return DEFAULT_AUTO_LEVEL_PREFIX;
  }
  const normalized = username
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (!normalized) {
    return DEFAULT_AUTO_LEVEL_PREFIX;
  }
  return `${normalized}-LEVEL`;
}

function parseAutoLevelIndex(levelCode, levelPrefix) {
  if (typeof levelCode !== "string") return null;
  const prefixPattern = escapeRegex(levelPrefix.toUpperCase());
  const match = levelCode
    .trim()
    .toUpperCase()
    .match(new RegExp(`^${prefixPattern}(\\d+)$`));
  if (!match) return null;
  return Number.parseInt(match[1], 10);
}

function getNextAutoLevelCode(levels, levelPrefix) {
  let maxIndex = -1;
  levels.forEach((level) => {
    const index = parseAutoLevelIndex(level.level_code, levelPrefix);
    if (Number.isInteger(index)) {
      maxIndex = Math.max(maxIndex, index);
    }
  });
  return `${levelPrefix}${maxIndex + 1}`;
}

function buildAutomaticTechnicalId() {
  const randomUuid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 12)}`;
  return `builder-${randomUuid}`.toLowerCase().slice(0, 80);
}

function buildDraftFingerprint(level, categoryName) {
  return JSON.stringify({
    level: level ?? null,
    categoryName: normalizeOptionalText(categoryName) ?? ""
  });
}

function App({ token, currentUser, onLogout, activeEnvironment, showEnvironmentUi }) {
  const [levels, setLevels] = useState(() => [createFreshLevel("level_0")]);
  const [activeLevelIndex, setActiveLevelIndex] = useState(0);
  const [activePhaseIndex, setActivePhaseIndex] = useState(0);
  const [activeLayer, setActiveLayer] = useState(LAYER.CENTER);
  const [selectedPiece, setSelectedPiece] = useState("RandomElement");
  const [selectedAlchemyColor, setSelectedAlchemyColor] = useState(DEFAULT_ALCHEMY_COLOR);
  const [tool, setTool] = useState(TOOL.PAINT);
  const [selectedCell, setSelectedCell] = useState({ collumn: 0, line: 0 });
  const [painting, setPainting] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [status, setStatus] = useState("Ready");
  const [toastMessage, setToastMessage] = useState("");
  const [leftPanelWidth, setLeftPanelWidth] = useState(320);
  const [paletteWidth, setPaletteWidth] = useState(420);
  const [isCompactLayout, setIsCompactLayout] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_LAYOUT_BREAKPOINT : false
  );
  const [mobileLeftPanelOpen, setMobileLeftPanelOpen] = useState(false);
  const [cloudLevels, setCloudLevels] = useState([]);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudBusy, setCloudBusy] = useState(false);
  const [selectedCloudLevelId, setSelectedCloudLevelId] = useState("");
  const [adminOtherLevels, setAdminOtherLevels] = useState([]);
  const [adminOtherLevelsLoading, setAdminOtherLevelsLoading] = useState(false);
  const [selectedAdminOtherLevelId, setSelectedAdminOtherLevelId] = useState("");
  const [levelCategoryName, setLevelCategoryName] = useState("");
  const [savedLevelSearch, setSavedLevelSearch] = useState("");
  const [savedLevelDropdownOpen, setSavedLevelDropdownOpen] = useState(false);
  const [passwordPanelOpen, setPasswordPanelOpen] = useState(false);
  const [currentPasswordDraft, setCurrentPasswordDraft] = useState("");
  const [newPasswordDraft, setNewPasswordDraft] = useState("");
  const [confirmPasswordDraft, setConfirmPasswordDraft] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [paletteContent, setPaletteContent] = useState(PALETTE_CONTENT.BOARD);
  const boardScrollRef = useRef(null);
  const zoomTargetRef = useRef(null);
  const resizeStateRef = useRef(null);
  const savedLevelDropdownRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const wasBoardPaletteLockedRef = useRef(false);
  const paletteContentBeforeBoardLockRef = useRef(PALETTE_CONTENT.BOARD);

  const activeLevel = levels[activeLevelIndex] ?? levels[0];
  const [savedDraftFingerprint, setSavedDraftFingerprint] = useState(() =>
    buildDraftFingerprint(activeLevel, "")
  );
  const selectedCloudLevel = useMemo(
    () => cloudLevels.find((level) => level.id === selectedCloudLevelId) ?? null,
    [cloudLevels, selectedCloudLevelId]
  );
  const selectedAdminCloudLevel = useMemo(
    () => adminOtherLevels.find((level) => level.id === selectedCloudLevelId) ?? null,
    [adminOtherLevels, selectedCloudLevelId]
  );
  const selectedLevelMetadata = selectedCloudLevel ?? selectedAdminCloudLevel ?? null;
  const isSelectedOwnCloudLevel = Boolean(selectedCloudLevelId && selectedCloudLevel);
  const selectedSavedLevelLabel = useMemo(() => {
    if (!selectedLevelMetadata) return "Select saved level";
    if (selectedAdminCloudLevel && !isSelectedOwnCloudLevel) {
      const creator = getCreatorDisplayName(selectedAdminCloudLevel);
      const levelName =
        selectedAdminCloudLevel.display_name || selectedAdminCloudLevel.level_code || "level";
      return `${creator} - ${levelName}`;
    }
    return (
      selectedLevelMetadata.display_name ||
      selectedLevelMetadata.level_code ||
      "Select saved level"
    );
  }, [isSelectedOwnCloudLevel, selectedAdminCloudLevel, selectedLevelMetadata]);
  const savedLevelCount = cloudLevels.length;
  const maxSavedLevels = Number.isFinite(Number(currentUser?.max_levels))
    ? Number(currentUser.max_levels)
    : null;
  const isLevelLimitReached = maxSavedLevels != null && savedLevelCount >= maxSavedLevels;
  const isAdminUser = currentUser?.role === "admin" || currentUser?.role === "mod";
  const isAdministrator = currentUser?.role === "admin";
  const showEnvironment =
    showEnvironmentUi && Boolean(activeEnvironment?.label) && !isProductionEnvironment(activeEnvironment);
  const isAnyMobileDrawerOpen = isCompactLayout && mobileLeftPanelOpen;
  const autoLevelPrefix = useMemo(
    () => getAutoLevelPrefixForUsername(currentUser?.username),
    [currentUser?.username]
  );
  const nextAutoLevelCode = useMemo(
    () => getNextAutoLevelCode(cloudLevels, autoLevelPrefix),
    [cloudLevels, autoLevelPrefix]
  );
  const filteredCloudLevels = useMemo(() => {
    const query = savedLevelSearch.trim().toLowerCase();
    if (!query) return cloudLevels;
    return cloudLevels.filter((level) => {
      const code = String(level.level_code || "").toLowerCase();
      const type = String(level.gameplay_type || "").toLowerCase();
      const category = String(level.category_name || "").toLowerCase();
      return code.includes(query) || type.includes(query) || category.includes(query);
    });
  }, [cloudLevels, savedLevelSearch]);
  const draftFingerprint = useMemo(
    () => buildDraftFingerprint(activeLevel, levelCategoryName),
    [activeLevel, levelCategoryName]
  );
  const hasUnsavedChanges = draftFingerprint !== savedDraftFingerprint;
  const isMalikFight = isMalikFightLevel(activeLevel);
  const baseBoard = activeLevel.board;
  const activeBoard =
    activePhaseIndex === 0
      ? baseBoard
      : activeLevel.malikBoardPhases[activePhaseIndex - 1] ?? baseBoard;
  const displayBottomBoard =
    isMalikFight && activePhaseIndex > 0 ? baseBoard : activeBoard;
  const isEditingMalikPhaseTopOnly = isMalikFight && activePhaseIndex > 0;
  const isBoardPaletteLocked = isMalikFight && activePhaseIndex > 0;
  const effectivePaletteContent = isBoardPaletteLocked
    ? PALETTE_CONTENT.MALIK
    : paletteContent;
  const isMalikPaletteMode =
    isMalikFight && effectivePaletteContent === PALETTE_CONTENT.MALIK;
  const displayedLayer = isMalikPaletteMode ? LAYER.CENTER : activeLayer;
  const visibleLayers = isMalikPaletteMode ? [LAYER.CENTER] : LAYER_ORDER;

  const phaseCount = 1 + (activeLevel.malikBoardPhases?.length ?? 0);
  const palette = useMemo(() => {
    const layerPalette = getPaletteForLayer(displayedLayer);
    if (displayedLayer !== LAYER.CENTER) {
      return layerPalette;
    }

    const hideLegacyAlchemyStages = (piece) => !LEGACY_ALCHEMY_STAGE_TYPES.has(piece);

    if (!isMalikFight) {
      return layerPalette.filter((piece) => !isMalikEnemyPiece(piece) && hideLegacyAlchemyStages(piece));
    }

    if (effectivePaletteContent === PALETTE_CONTENT.MALIK) {
      return layerPalette.filter(
        (piece) => piece === "Empty" || piece === "NoElement" || isMalikEnemyPiece(piece)
      );
    }

    return layerPalette.filter((piece) => !isMalikEnemyPiece(piece) && hideLegacyAlchemyStages(piece));
  }, [displayedLayer, effectivePaletteContent, isMalikFight]);
  const malikTopPieces = useMemo(
    () => activeBoard.pieces.filter((piece) => piece.line < MALIK_LAYOUT.GAP_ROW),
    [activeBoard]
  );
  const malikBottomPieces = useMemo(
    () => displayBottomBoard.pieces.filter((piece) => piece.line >= MALIK_LAYOUT.BOTTOM_START_ROW),
    [displayBottomBoard]
  );
  const activeBoardFootprints = useMemo(() => buildAnchorFootprintMap(activeBoard), [activeBoard]);
  const displayBottomFootprints = useMemo(
    () =>
      displayBottomBoard === activeBoard
        ? activeBoardFootprints
        : buildAnchorFootprintMap(displayBottomBoard),
    [activeBoard, activeBoardFootprints, displayBottomBoard]
  );
  const threatTargets = useMemo(
    () => (isMalikFight ? [] : buildThreatTargets(activeBoard)),
    [activeBoard, isMalikFight]
  );
  const malikTargetSummary = useMemo(
    () =>
      isMalikFight
        ? buildMalikTargetSummary(malikTopPieces)
        : { targets: [], spawnedTargets: [], totalHp: 0 },
    [isMalikFight, malikTopPieces]
  );
  const displayedTargets = isMalikFight
    ? [...malikTargetSummary.targets, ...malikTargetSummary.spawnedTargets]
    : threatTargets;
  const shouldShowTargetsFloat = isMalikFight || threatTargets.length > 0;
  const selectedIndex = getPieceIndex(
    activeBoard,
    Math.max(0, Math.min(selectedCell.collumn, activeBoard.collumn - 1)),
    Math.max(0, Math.min(selectedCell.line, activeBoard.line - 1))
  );
  const selectedPieceData = activeBoard.pieces[selectedIndex] ?? activeBoard.pieces[0];
  const treasureMapSharedQuantity = useMemo(
    () => getSharedThreatQuantity(activeBoard, "TreasureMap"),
    [activeBoard]
  );

  useEffect(() => {
    if (activeLevelIndex >= levels.length) {
      setActiveLevelIndex(Math.max(0, levels.length - 1));
    }
  }, [activeLevelIndex, levels.length]);

  useEffect(() => {
    if (activePhaseIndex >= phaseCount) {
      setActivePhaseIndex(Math.max(0, phaseCount - 1));
    }
  }, [activePhaseIndex, phaseCount]);

  useEffect(() => {
    if (!isEditingMalikPhaseTopOnly) return;
    if (selectedCell.line < MALIK_LAYOUT.BOTTOM_START_ROW) return;
    setSelectedCell({ collumn: 0, line: 0 });
  }, [isEditingMalikPhaseTopOnly, selectedCell.line]);

  useEffect(() => {
    if (palette.includes(selectedPiece)) return;
    setSelectedPiece(palette[0] ?? "NoElement");
  }, [palette, selectedPiece]);

  useEffect(() => {
    if (!isMalikPaletteMode) return;
    if (activeLayer === LAYER.CENTER) return;
    setActiveLayer(LAYER.CENTER);
  }, [activeLayer, isMalikPaletteMode]);

  useEffect(() => {
    if (isMalikFight) return;
    if (paletteContent === PALETTE_CONTENT.BOARD) return;
    setPaletteContent(PALETTE_CONTENT.BOARD);
  }, [isMalikFight, paletteContent]);

  useEffect(() => {
    const wasLocked = wasBoardPaletteLockedRef.current;

    if (isBoardPaletteLocked && !wasLocked) {
      paletteContentBeforeBoardLockRef.current = paletteContent;
      if (paletteContent !== PALETTE_CONTENT.MALIK) {
        setPaletteContent(PALETTE_CONTENT.MALIK);
      }
    } else if (!isBoardPaletteLocked && wasLocked && isMalikFight) {
      const restoreContent = paletteContentBeforeBoardLockRef.current;
      if (restoreContent !== paletteContent) {
        setPaletteContent(restoreContent);
      }
    }

    wasBoardPaletteLockedRef.current = isBoardPaletteLocked;
  }, [isBoardPaletteLocked, isMalikFight, paletteContent]);

  useEffect(() => {
    function onMouseDown(event) {
      if (!savedLevelDropdownOpen) return;
      if (!savedLevelDropdownRef.current?.contains(event.target)) {
        setSavedLevelDropdownOpen(false);
      }
    }

    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [savedLevelDropdownOpen]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const loadCloudLevels = useCallback(
    async (preferredSelectionId = "") => {
      if (!token) return;

      setCloudLoading(true);
      try {
        const payload = await listWebBuilderLevels(token);
        const nextLevels = Array.isArray(payload) ? payload : [];
        setCloudLevels(nextLevels);
        setSelectedCloudLevelId((previousSelection) => {
          if (
            preferredSelectionId &&
            nextLevels.some((level) => level.id === preferredSelectionId)
          ) {
            return preferredSelectionId;
          }
          if (
            previousSelection &&
            nextLevels.some((level) => level.id === previousSelection)
          ) {
            return previousSelection;
          }
          return "";
        });
      } catch (error) {
        announce(`Could not load saved levels: ${error.message}`);
      } finally {
        setCloudLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    loadCloudLevels();
  }, [loadCloudLevels]);

  const loadAdminOtherLevels = useCallback(
    async (preferredSelectionId = "") => {
      if (!token || !isAdministrator) {
        setAdminOtherLevels([]);
        setSelectedAdminOtherLevelId("");
        return;
      }

      setAdminOtherLevelsLoading(true);
      try {
        const payload = await listAdminWebBuilderLevels(token, { limit: 200, offset: 0 });
        const ownUserId = currentUser?.id;
        const nextLevels = Array.isArray(payload)
          ? payload.filter((level) => level.created_by_user_id !== ownUserId)
          : [];

        nextLevels.sort((left, right) => {
          const leftCreator = String(getCreatorDisplayName(left)).toLowerCase();
          const rightCreator = String(getCreatorDisplayName(right)).toLowerCase();
          if (leftCreator !== rightCreator) {
            return leftCreator.localeCompare(rightCreator);
          }
          return String(left.level_code || "").localeCompare(String(right.level_code || ""));
        });

        setAdminOtherLevels(nextLevels);
        setSelectedAdminOtherLevelId((previousSelection) => {
          if (
            preferredSelectionId &&
            nextLevels.some((level) => level.id === preferredSelectionId)
          ) {
            return preferredSelectionId;
          }
          if (
            previousSelection &&
            nextLevels.some((level) => level.id === previousSelection)
          ) {
            return previousSelection;
          }
          return "";
        });
      } catch (error) {
        announce(`Could not load creator levels: ${error.message}`);
      } finally {
        setAdminOtherLevelsLoading(false);
      }
    },
    [currentUser?.id, isAdministrator, token]
  );

  useEffect(() => {
    loadAdminOtherLevels();
  }, [loadAdminOtherLevels]);

  useEffect(() => {
    function handleResize() {
      const compact = window.innerWidth <= MOBILE_LAYOUT_BREAKPOINT;
      setIsCompactLayout(compact);
      if (!compact) {
        setMobileLeftPanelOpen(false);
      }
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("is-mobile-drawer-open", isAnyMobileDrawerOpen);
    return () => {
      document.body.classList.remove("is-mobile-drawer-open");
    };
  }, [isAnyMobileDrawerOpen]);

  async function submitPasswordChange() {
    setPasswordError("");

    if (!currentPasswordDraft) {
      setPasswordError("Current password is required.");
      return;
    }

    if (newPasswordDraft.length < 6) {
      setPasswordError("Password must have at least 6 characters.");
      return;
    }

    if (newPasswordDraft !== confirmPasswordDraft) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setPasswordBusy(true);
    try {
      await changeOwnWebBuilderPassword(token, currentPasswordDraft, newPasswordDraft);
      setCurrentPasswordDraft("");
      setNewPasswordDraft("");
      setConfirmPasswordDraft("");
      setPasswordPanelOpen(false);
      announce("Password updated.");
    } catch (error) {
      setPasswordError(error.message || "Could not update password.");
    } finally {
      setPasswordBusy(false);
    }
  }

  const fitBoardZoom = useCallback(() => {
    const scrollContainer = boardScrollRef.current;
    const zoomTarget = zoomTargetRef.current;
    if (!scrollContainer || !zoomTarget) return;

    const availableWidth = scrollContainer.clientWidth;
    const availableHeight = scrollContainer.clientHeight;
    const boardWidth = zoomTarget.offsetWidth;
    const boardHeight = zoomTarget.offsetHeight;
    if (availableWidth <= 0 || availableHeight <= 0 || boardWidth <= 0 || boardHeight <= 0) {
      return;
    }

    const fitZoom = Math.min(availableWidth / boardWidth, availableHeight / boardHeight);
    if (!Number.isFinite(fitZoom) || fitZoom <= 0) return;

    const nextZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));
    setZoom((previous) => {
      const rounded = Number(nextZoom.toFixed(2));
      return Math.abs(previous - rounded) < 0.01 ? previous : rounded;
    });

    if (isCompactLayout) {
      window.requestAnimationFrame(() => {
        const centeredOffset = Math.max(0, (zoomTarget.offsetWidth - scrollContainer.clientWidth) / 2);
        scrollContainer.scrollLeft = centeredOffset;
      });
    }
  }, [isCompactLayout]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(fitBoardZoom);
    return () => window.cancelAnimationFrame(frame);
  }, [
    fitBoardZoom,
    activeLevelIndex,
    activePhaseIndex,
    isMalikFight,
    activeBoard.collumn,
    activeBoard.line,
    displayBottomBoard.collumn,
    displayBottomBoard.line
  ]);

  useEffect(() => {
    window.addEventListener("resize", fitBoardZoom);
    return () => window.removeEventListener("resize", fitBoardZoom);
  }, [fitBoardZoom]);

  useEffect(() => {
    function handleMouseUp() {
      setPainting(false);
    }
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  useEffect(() => {
    function onMouseMove(event) {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;

      if (resizeState.kind === "left") {
        const nextWidth = resizeState.startWidth + (event.clientX - resizeState.startX);
        setLeftPanelWidth(clamp(nextWidth, MIN_LEFT_PANEL_WIDTH, MAX_LEFT_PANEL_WIDTH));
        return;
      }

      if (resizeState.kind === "palette") {
        const nextWidth = resizeState.startWidth - (event.clientX - resizeState.startX);
        setPaletteWidth(clamp(nextWidth, MIN_PALETTE_WIDTH, MAX_PALETTE_WIDTH));
      }
    }

    function onMouseUp() {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      document.body.classList.remove("is-resizing-columns");
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.classList.remove("is-resizing-columns");
    };
  }, []);

  function announce(message) {
    setStatus(message);
  }

  function showToast(message) {
    if (!message) return;
    setToastMessage(message);
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setToastMessage("");
      toastTimeoutRef.current = null;
    }, 2600);
  }

  function confirmDiscardUnsavedChanges() {
    if (!hasUnsavedChanges) return true;
    return window.confirm(
      "You have unsaved changes. Load another level and lose those unsaved edits?"
    );
  }

  function beginColumnResize(kind, event) {
    event.preventDefault();
    resizeStateRef.current = {
      kind,
      startX: event.clientX,
      startWidth: kind === "left" ? leftPanelWidth : paletteWidth
    };
    document.body.classList.add("is-resizing-columns");
  }

  function updateActiveLevel(mutator) {
    setLevels((previous) => {
      const next = [...previous];
      const current = structuredClone(next[activeLevelIndex]);
      mutator(current);
      next[activeLevelIndex] = normalizeLevel(current, activeLevelIndex);
      return next;
    });
  }

  function readPhaseBoard(level) {
    if (activePhaseIndex <= 0) return level.board;
    return level.malikBoardPhases[activePhaseIndex - 1] ?? level.board;
  }

  function isEditableCell(line) {
    if (!isMalikFight) return true;
    if (isMalikSeparatorRow(line)) return false;
    if (activePhaseIndex <= 0) return true;
    return line < MALIK_LAYOUT.BOTTOM_START_ROW;
  }

  function canPlaceCenterPieceAtLine(pieceType, line, showMessage = true) {
    if (!isMalikFight) return true;
    if (isMalikSeparatorRow(line)) {
      if (showMessage) {
        announce("Row 5 is locked and cannot be edited.");
      }
      return false;
    }

    const normalized = normalizePieceType(pieceType, "NoElement");
    const placingMalikEnemy = isMalikEnemyPiece(normalized);
    const isTopRow = line < MALIK_LAYOUT.GAP_ROW;
    const size = getPieceSize(normalized);
    const lastOccupiedLine = line + size.height - 1;

    if (isTopRow) {
      const allowed = isMalikTopCenterPieceAllowed(normalized);
      if (!allowed && showMessage) {
        announce("Top Malik grid accepts Malik enemies (plus Empty or NoElement).");
      }
      if (!allowed) return false;

      if (placingMalikEnemy && lastOccupiedLine >= MALIK_LAYOUT.GAP_ROW) {
        if (showMessage) {
          announce("Malik enemies must fit fully inside the top grid (rows 0-4).");
        }
        return false;
      }

      return true;
    }

    if (placingMalikEnemy) {
      if (showMessage) {
        announce("Malik enemies can only be placed in the top grid (rows 0-4).");
      }
      return false;
    }

    return true;
  }

  function canPaintLayerAtLine(layer, line, showMessage = true) {
    if (!isMalikFight) return true;
    if (isMalikSeparatorRow(line)) {
      if (showMessage) {
        announce("Row 5 is locked and cannot be edited.");
      }
      return false;
    }
    if (layer === LAYER.CENTER) return true;
    if (line >= MALIK_LAYOUT.BOTTOM_START_ROW) return true;

    if (showMessage) {
      announce("Background and foreground pieces only belong on the threat board (rows 6-12).");
    }

    return false;
  }

  function initializeMalikLayout() {
    updateActiveLevel((level) => {
      level.gameplayType = "MalikFight";
      level.board = createMalikBoardTemplate();
      level.malikBoardPhases = [];
    });
    setActivePhaseIndex(0);
    setSelectedCell({ collumn: 0, line: 0 });
    setActiveLayer(LAYER.CENTER);
    setSelectedPiece("RandomElement");
    announce("Malik board initialized to fixed 9x13 layout.");
  }

  function setGameplayType(nextGameplayType) {
    if (!isAdminUser) {
      announce("Only admins can change Gameplay Type.");
      return;
    }

    updateActiveLevel((level) => {
      level.gameplayType = nextGameplayType;

      if (nextGameplayType !== "MalikFight") {
        level.board = createThreatClearBoardTemplate(9, 9);
        level.malikBoardPhases = [];
        return;
      }

      if (!isMalikBoardFixed(level.board)) {
        level.board = createMalikBoardTemplate();
      }

      if (!Array.isArray(level.malikBoardPhases)) {
        level.malikBoardPhases = [];
      } else {
        level.malikBoardPhases = level.malikBoardPhases.map((phase) =>
          isMalikBoardFixed(phase)
            ? phase
            : resizeBoard(phase ?? level.board, MALIK_LAYOUT.COLUMNS, MALIK_LAYOUT.ROWS)
        );
      }
    });

    if (nextGameplayType === "MalikFight") {
      setActivePhaseIndex(0);
      setSelectedCell({ collumn: 0, line: 0 });
      setActiveLayer(LAYER.CENTER);
      setSelectedPiece("RandomElement");
      announce("Malik mode enabled (fixed 9x13 board, top rows 0-4).");
    } else {
      setActivePhaseIndex(0);
      setSelectedCell({ collumn: 0, line: 0 });
      setActiveLayer(LAYER.CENTER);
      setSelectedPiece("RandomElement");
      announce("Gameplay type set to ThreatClear. Board reset to 9x9 RandomElement tiles.");
    }
  }

  function ensureCenterWhenOverlaysExist(piece) {
    if (
      (piece.backgroundType !== "Empty" || piece.foregroundType !== "Empty") &&
      piece.type === "Empty"
    ) {
      piece.type = "NoElement";
    }
  }

  function resolveAnchorFootprint(board, anchorCollumn, anchorLine) {
    if (
      anchorCollumn < 0 ||
      anchorLine < 0 ||
      anchorCollumn >= board.collumn ||
      anchorLine >= board.line
    ) {
      return { width: 1, height: 1 };
    }

    const anchorIndex = getPieceIndex(board, anchorCollumn, anchorLine);
    const anchorPiece = board.pieces[anchorIndex];
    const baseSize = getPieceSize(anchorPiece.type);
    let maxCollumn = anchorCollumn + baseSize.width - 1;
    let maxLine = anchorLine + baseSize.height - 1;

    board.pieces.forEach((piece) => {
      if (piece.type !== "PartOf") return;
      if (piece.parent?.y !== anchorCollumn || piece.parent?.x !== anchorLine) return;
      maxCollumn = Math.max(maxCollumn, piece.collumn);
      maxLine = Math.max(maxLine, piece.line);
    });

    return {
      width: Math.max(1, maxCollumn - anchorCollumn + 1),
      height: Math.max(1, maxLine - anchorLine + 1)
    };
  }

  function clearAnchorArea(board, anchorCollumn, anchorLine, footprint = null) {
    const anchorIndex = getPieceIndex(board, anchorCollumn, anchorLine);
    const anchorPiece = board.pieces[anchorIndex];
    const { width, height } = footprint ?? resolveAnchorFootprint(board, anchorCollumn, anchorLine);

    for (let row = 0; row < height; row += 1) {
      for (let col = 0; col < width; col += 1) {
        const c = anchorCollumn + col;
        const r = anchorLine + row;
        if (c < 0 || r < 0 || c >= board.collumn || r >= board.line) continue;
        const idx = getPieceIndex(board, c, r);
        const piece = board.pieces[idx];
        piece.type = "NoElement";
        piece.startImune = false;
        piece.parent = { x: r, y: c };
        ensureCenterWhenOverlaysExist(piece);
      }
    }
  }

  function clearExistingMultiTile(board, collumn, line) {
    const idx = getPieceIndex(board, collumn, line);
    const piece = board.pieces[idx];
    if (piece.type === "PartOf") {
      const anchorCollumn = piece.parent?.y;
      const anchorLine = piece.parent?.x;
      if (
        Number.isInteger(anchorCollumn) &&
        Number.isInteger(anchorLine) &&
        anchorCollumn >= 0 &&
        anchorLine >= 0 &&
        anchorCollumn < board.collumn &&
        anchorLine < board.line
      ) {
        clearAnchorArea(
          board,
          anchorCollumn,
          anchorLine,
          resolveAnchorFootprint(board, anchorCollumn, anchorLine)
        );
      }
      return;
    }

    const size = resolveAnchorFootprint(board, collumn, line);
    if (size.width > 1 || size.height > 1) {
      clearAnchorArea(board, collumn, line, size);
    }
  }

  function paintCenterPiece(board, collumn, line, pieceType) {
    const alchemyPaletteSelection = parseAlchemyPaletteType(pieceType);
    const hiddenWormSelection = pieceType === HIDDEN_WORM_PROXY_TYPE;
    const normalized = normalizePieceType(
      hiddenWormSelection
        ? "Worm_1"
        : alchemyPaletteSelection
          ? `AlchemyBox_${alchemyPaletteSelection.stage}`
          : pieceType,
      "NoElement"
    );
    const sharedThreatQuantity = usesSharedThreatQuantity(normalized)
      ? getSharedThreatQuantity(board, normalized) ?? getPieceMinSharedThreatQuantity(normalized)
      : null;

    if (normalized === "Empty") {
      const idx = getPieceIndex(board, collumn, line);
      const piece = board.pieces[idx];
      piece.type = "Empty";
      piece.startImune = false;
      piece.parent = { x: line, y: collumn };
      ensureCenterWhenOverlaysExist(piece);
      return true;
    }

    const size = getPieceSize(normalized);
    if (collumn + size.width > board.collumn || line + size.height > board.line) {
      announce("Cannot place this tile here (out of bounds).");
      return false;
    }

    for (let row = 0; row < size.height; row += 1) {
      for (let col = 0; col < size.width; col += 1) {
        clearExistingMultiTile(board, collumn + col, line + row);
      }
    }

    for (let row = 0; row < size.height; row += 1) {
      for (let col = 0; col < size.width; col += 1) {
        const targetCollumn = collumn + col;
        const targetLine = line + row;
        const idx = getPieceIndex(board, targetCollumn, targetLine);
        const piece = board.pieces[idx];

        if (row === 0 && col === 0) {
          piece.type = normalized;
          piece.startImune = isWormType(normalized) ? hiddenWormSelection : false;
          piece.parent = { x: line, y: collumn };
          if (isAlchemyBoxType(normalized)) {
            piece.pieceColor = normalizeAlchemyColor(
              alchemyPaletteSelection?.pieceColor ?? selectedAlchemyColor
            );
          }
        } else {
          piece.type = "PartOf";
          piece.startImune = false;
          piece.parent = { x: line, y: collumn };
        }
      }
    }

    if (sharedThreatQuantity != null) {
      syncSharedThreatQuantity(board, normalized, sharedThreatQuantity);
    }

    return true;
  }

  function paintLayerAtCell(board, collumn, line, layer, pieceType) {
    const idx = getPieceIndex(board, collumn, line);
    const piece = board.pieces[idx];
    const field = getLayerField(layer);
    const fallback = layer === LAYER.CENTER ? "NoElement" : "Empty";
    const normalized = normalizePieceType(pieceType, fallback);

    if (layer === LAYER.CENTER) {
      return paintCenterPiece(board, collumn, line, normalized);
    }

    piece[field] = normalized;
    ensureCenterWhenOverlaysExist(piece);
    return true;
  }

  function eraseLayerAtCell(board, collumn, line, layer) {
    const idx = getPieceIndex(board, collumn, line);
    const piece = board.pieces[idx];

    if (layer === LAYER.CENTER) {
      clearExistingMultiTile(board, collumn, line);
      piece.type = "NoElement";
      piece.startImune = false;
      piece.parent = { x: line, y: collumn };
    } else if (layer === LAYER.BACKGROUND) {
      piece.backgroundType = "Empty";
    } else {
      piece.foregroundType = "Empty";
    }

    ensureCenterWhenOverlaysExist(piece);
  }

  function pickFromCell(collumn, line) {
    const idx = getPieceIndex(activeBoard, collumn, line);
    const piece = activeBoard.pieces[idx];
    const field = getLayerField(displayedLayer);
    let value = piece[field];
    let sourcePiece = piece;

    if (displayedLayer === LAYER.CENTER && value === "PartOf") {
      const anchorCollumn = piece.parent?.y ?? collumn;
      const anchorLine = piece.parent?.x ?? line;
      const anchorIndex = getPieceIndex(activeBoard, anchorCollumn, anchorLine);
      const anchorPiece = activeBoard.pieces[anchorIndex];
      sourcePiece = anchorPiece ?? piece;
      value = anchorPiece?.type ?? value;
    }

    if (value) {
      if (
        displayedLayer === LAYER.CENTER &&
        isWormType(value) &&
        Boolean(sourcePiece?.startImune)
      ) {
        setSelectedPiece(HIDDEN_WORM_PROXY_TYPE);
      } else if (displayedLayer === LAYER.CENTER && isAlchemyBoxType(value)) {
        const pickedColor = normalizeAlchemyColor(sourcePiece?.pieceColor);
        setSelectedAlchemyColor(pickedColor);
        const family = ALCHEMY_FAMILY_BY_COLOR[pickedColor] ?? "Breaker";
        const stage = getAlchemyStage(value);
        setSelectedPiece(`AlchemyBox_${family}_${stage}`);
      } else {
        setSelectedPiece(value);
      }
      setTool(TOOL.PAINT);
      announce(`Picked ${value}`);
    }
  }

  function applyAtCell(collumn, line, overrideTool) {
    if (!isEditableCell(line)) {
      if (isMalikSeparatorRow(line)) {
        announce("Row 5 is locked and cannot be edited.");
      } else {
        announce("While editing Malik phases, only the top grid (rows 0-4) is editable.");
      }
      return;
    }

    const mode = overrideTool || tool;
    if (mode === TOOL.PICK) {
      pickFromCell(collumn, line);
      setSelectedCell({ collumn, line });
      return;
    }

    if (mode !== TOOL.ERASE && !canPaintLayerAtLine(displayedLayer, line)) {
      return;
    }

    if (
      mode !== TOOL.ERASE &&
      displayedLayer === LAYER.CENTER &&
      !canPlaceCenterPieceAtLine(selectedPiece, line)
    ) {
      return;
    }

    const preservedSharedTotals =
      displayedLayer === LAYER.CENTER ? collectSharedThreatTotals(activeBoard) : new Map();

    updateActiveLevel((level) => {
      const board = readPhaseBoard(level);
      if (mode === TOOL.ERASE) {
        eraseLayerAtCell(board, collumn, line, displayedLayer);
      } else {
        paintLayerAtCell(board, collumn, line, displayedLayer, selectedPiece);
      }
      syncBoardSharedThreatQuantities(board, preservedSharedTotals);
    });

    setSelectedCell({ collumn, line });
  }

  function fillBoard() {
    let skippedPlacementCount = 0;
    let skippedThreatLayerCount = 0;
    const preservedSharedTotals =
      displayedLayer === LAYER.CENTER ? collectSharedThreatTotals(activeBoard) : new Map();

    updateActiveLevel((level) => {
      const board = readPhaseBoard(level);
      for (let row = 0; row < board.line; row += 1) {
        for (let col = 0; col < board.collumn; col += 1) {
          if (!isEditableCell(row)) continue;
          if (tool === TOOL.ERASE) {
            eraseLayerAtCell(board, col, row, displayedLayer);
          } else {
            if (!canPaintLayerAtLine(displayedLayer, row, false)) {
              skippedThreatLayerCount += 1;
              continue;
            }
            if (
              displayedLayer === LAYER.CENTER &&
              !canPlaceCenterPieceAtLine(selectedPiece, row, false)
            ) {
              skippedPlacementCount += 1;
              continue;
            }
            paintLayerAtCell(board, col, row, displayedLayer, selectedPiece);
          }
        }
      }
      syncBoardSharedThreatQuantities(board, preservedSharedTotals);
    });

    if (skippedPlacementCount > 0 || skippedThreatLayerCount > 0) {
      const reasons = [];
      if (skippedPlacementCount > 0) {
        reasons.push(
          `${skippedPlacementCount} skipped cell(s) due to Malik center-piece placement rules`
        );
      }
      if (skippedThreatLayerCount > 0) {
        reasons.push(
          `${skippedThreatLayerCount} skipped cell(s) because background/foreground only paint on rows 6-12`
        );
      }
      announce(`Fill completed with ${reasons.join(" and ")}.`);
      return;
    }

    announce(
      isEditingMalikPhaseTopOnly ? "Filled Malik top grid for this phase." : "Board filled."
    );
  }

  function onCellMouseDown(collumn, line) {
    if (!isEditableCell(line)) {
      if (isMalikSeparatorRow(line)) {
        announce("Row 5 is locked and cannot be edited.");
      } else {
        announce("While editing Malik phases, only the top grid (rows 0-4) is editable.");
      }
      return;
    }

    setPainting(true);
    if (tool === TOOL.FILL) {
      fillBoard();
      return;
    }
    applyAtCell(collumn, line);
  }

  function onCellMouseEnter(collumn, line) {
    if (!painting) return;
    if (!isEditableCell(line)) return;
    if (tool === TOOL.PAINT || tool === TOOL.ERASE) {
      applyAtCell(collumn, line);
    }
  }

  function addPhaseFromCurrent() {
    updateActiveLevel((level) => {
      const sourceBoard = structuredClone(readPhaseBoard(level));
      level.malikBoardPhases.push(sourceBoard);
    });
    setActivePhaseIndex(phaseCount);
    announce("Malik phase created from current board.");
  }

  function removeCurrentPhase() {
    if (activePhaseIndex === 0) {
      announce("Base board cannot be removed.");
      return;
    }
    updateActiveLevel((level) => {
      level.malikBoardPhases.splice(activePhaseIndex - 1, 1);
    });
    setActivePhaseIndex((current) => Math.max(0, current - 1));
    announce("Malik phase removed.");
  }

  function updateBoardSize(nextCollumn, nextLine) {
    if (isMalikFight) {
      announce("Malik levels use a fixed 9x13 board.");
      return;
    }

    const preservedSharedTotals = collectSharedThreatTotals(activeBoard);
    updateActiveLevel((level) => {
      if (activePhaseIndex === 0) {
        level.board = resizeBoard(level.board, nextCollumn, nextLine);
        syncBoardSharedThreatQuantities(level.board, preservedSharedTotals);
      } else {
        level.malikBoardPhases[activePhaseIndex - 1] = resizeBoard(
          level.malikBoardPhases[activePhaseIndex - 1],
          nextCollumn,
          nextLine
        );
        syncBoardSharedThreatQuantities(
          level.malikBoardPhases[activePhaseIndex - 1],
          preservedSharedTotals
        );
      }
    });
  }

  function useCloudLevelMetadata(levelSummary) {
    setSelectedCloudLevelId(levelSummary.id);
    setLevelCategoryName(levelSummary.category_name || "");
    setSavedLevelDropdownOpen(false);
  }

  async function selectAndLoadCloudLevel(levelSummary) {
    if (!confirmDiscardUnsavedChanges()) return;
    useCloudLevelMetadata(levelSummary);
    await loadCloudLevel(levelSummary.id);
  }

  async function loadSelectedAdminOtherLevel() {
    if (!token) {
      announce("Missing session token.");
      return;
    }
    if (!isAdministrator) {
      announce("Only admins can load creator levels.");
      return;
    }
    if (!selectedAdminOtherLevelId) {
      announce("Select a creator level first.");
      return;
    }
    if (!confirmDiscardUnsavedChanges()) return;

    setCloudBusy(true);
    try {
      const payload = await getAdminWebBuilderLevel(token, selectedAdminOtherLevelId);
      const normalizedLevel = normalizeLevel(payload.level_json, 0);
      setLevels([normalizedLevel]);
      setActiveLevelIndex(0);
      setActivePhaseIndex(0);
      setActiveLayer(LAYER.CENTER);
      setSelectedCell({ collumn: 0, line: 0 });
      setSelectedPiece("RandomElement");
      setSelectedCloudLevelId(payload.id);
      setLevelCategoryName(payload.category_name || "");
      setSavedDraftFingerprint(
        buildDraftFingerprint(normalizedLevel, payload.category_name || "")
      );
      setSavedLevelDropdownOpen(false);
      announce(`Loaded "${payload.level_code}" from ${getCreatorDisplayName(payload)}.`);
    } catch (error) {
      announce(`Load failed: ${error.message}`);
    } finally {
      setCloudBusy(false);
    }
  }

  function startNewCloudLevel() {
    if (isLevelLimitReached) {
      announce(`Level limit reached (${savedLevelCount}/${maxSavedLevels}).`);
      return;
    }
    setSelectedCloudLevelId("");
    setLevelCategoryName("");
    setSavedLevelSearch("");
    setSavedLevelDropdownOpen(false);
    announce(`New level mode. Next code: ${nextAutoLevelCode}`);
  }

  function buildSaveLevelJson(forcedId = null) {
    const levelForExport = structuredClone(activeLevel);
    if (typeof forcedId === "string" && forcedId.trim()) {
      levelForExport.id = forcedId.trim();
    }

    syncBoardSharedThreatQuantities(levelForExport.board);
    levelForExport.malikBoardPhases.forEach((phaseBoard) => syncBoardSharedThreatQuantities(phaseBoard));

    return buildSingleLevelExport(levelForExport);
  }

  function hasRequiredSpawner(level) {
    if (!level?.board?.pieces) return false;

    if (level.gameplayType === "MalikFight") {
      return level.board.pieces.some(
        (piece) => piece.type === "Spawner" && piece.line >= MALIK_LAYOUT.BOTTOM_START_ROW
      );
    }

    return level.board.pieces.some((piece) => piece.type === "Spawner");
  }

  function validateLevelBeforeSave() {
    if (hasRequiredSpawner(activeLevel)) return null;
    if (activeLevel.gameplayType === "MalikFight") {
      return "Malik levels require at least 1 Spawner in the Match Board (rows 6-12).";
    }
    return "Level requires at least 1 Spawner.";
  }

  async function loadCloudLevel(levelId) {
    if (!token) {
      announce("Missing session token.");
      return;
    }

    setCloudBusy(true);
    try {
      const payload = await getWebBuilderLevel(token, levelId);
      const normalizedLevel = normalizeLevel(payload.level_json, 0);
      setLevels([normalizedLevel]);
      setActiveLevelIndex(0);
      setActivePhaseIndex(0);
      setActiveLayer(LAYER.CENTER);
      setSelectedCell({ collumn: 0, line: 0 });
      setSelectedPiece("RandomElement");
      setSelectedCloudLevelId(payload.id);
      setLevelCategoryName(payload.category_name || "");
      setSavedDraftFingerprint(
        buildDraftFingerprint(normalizedLevel, payload.category_name || "")
      );
      announce(`Loaded saved level "${payload.level_code}".`);
    } catch (error) {
      announce(`Load failed: ${error.message}`);
    } finally {
      setCloudBusy(false);
    }
  }

  async function createCloudLevelEntry(draftFingerprintSnapshot = null) {
    if (!token) {
      announce("Missing session token.");
      return;
    }
    const validationError = validateLevelBeforeSave();
    if (validationError) {
      announce(validationError);
      return;
    }

    const levelCode = getNextAutoLevelCode(cloudLevels, autoLevelPrefix);
    const levelIdForSave = isAdminUser ? activeLevel.id : levelCode;
    const payload = {
      technical_id: buildAutomaticTechnicalId(),
      level_code: levelCode,
      display_name: levelCode,
      category_name: normalizeOptionalText(levelCategoryName),
      gameplay_type: activeLevel.gameplayType,
      level_json: buildSaveLevelJson(levelIdForSave),
      rating_score: 0
    };

    setCloudBusy(true);
    try {
      const created = await createWebBuilderLevel(token, payload);
      await loadCloudLevels(created.id);
      setSelectedCloudLevelId(created.id);
      setSavedDraftFingerprint(draftFingerprintSnapshot ?? buildDraftFingerprint(activeLevel, levelCategoryName));
      announce(`Created saved level "${created.level_code}".`);
      showToast(`Level "${created.level_code}" saved.`);
    } catch (error) {
      announce(`Create failed: ${error.message}`);
    } finally {
      setCloudBusy(false);
    }
  }

  async function saveCloudLevelEntry() {
    if (selectedCloudLevelId && !isSelectedOwnCloudLevel) {
      announce("Loaded creator levels are read-only here. Use New Level to save a copy.");
      return;
    }

    const saveLabel = selectedCloudLevelId
      ? selectedLevelMetadata?.level_code || "selected level"
      : nextAutoLevelCode;
    const confirmed = window.confirm(
      selectedCloudLevelId
        ? `Save changes to "${saveLabel}"?`
        : `Create and save new level "${saveLabel}"?`
    );
    if (!confirmed) return;

    const draftFingerprintSnapshot = buildDraftFingerprint(activeLevel, levelCategoryName);

    if (!selectedCloudLevelId) {
      await createCloudLevelEntry(draftFingerprintSnapshot);
      return;
    }
    await updateCloudLevelEntry(draftFingerprintSnapshot);
  }

  async function updateCloudLevelEntry(draftFingerprintSnapshot = null) {
    if (!token) {
      announce("Missing session token.");
      return;
    }
    if (!selectedCloudLevelId) {
      announce("Select a saved level first.");
      return;
    }
    if (!isSelectedOwnCloudLevel) {
      announce("Loaded creator levels are read-only here. Use New Level to save a copy.");
      return;
    }
    const validationError = validateLevelBeforeSave();
    if (validationError) {
      announce(validationError);
      return;
    }

    const payload = {
      category_name: normalizeOptionalText(levelCategoryName),
      gameplay_type: activeLevel.gameplayType,
      level_json: buildSaveLevelJson(
        isAdminUser ? activeLevel.id : selectedLevelMetadata?.level_code || nextAutoLevelCode
      )
    };

    setCloudBusy(true);
    try {
      const updated = await updateWebBuilderLevel(token, selectedCloudLevelId, payload);
      await loadCloudLevels(updated.id);
      if (isAdministrator) {
        await loadAdminOtherLevels();
      }
      setSavedDraftFingerprint(draftFingerprintSnapshot ?? buildDraftFingerprint(activeLevel, levelCategoryName));
      announce(`Updated saved level "${updated.level_code}".`);
      showToast(`Level "${updated.level_code}" saved.`);
    } catch (error) {
      announce(`Update failed: ${error.message}`);
    } finally {
      setCloudBusy(false);
    }
  }

  async function deleteCloudLevelEntry() {
    if (!token) {
      announce("Missing session token.");
      return;
    }
    if (!selectedCloudLevel || !isSelectedOwnCloudLevel) {
      announce("Select a saved level first.");
      return;
    }
    const confirmed = window.confirm(
      `Delete saved level "${selectedCloudLevel.level_code}"?`
    );
    if (!confirmed) return;

    setCloudBusy(true);
    try {
      await deleteWebBuilderLevel(token, selectedCloudLevel.id);
      setCloudLevels((previous) =>
        previous.filter((level) => level.id !== selectedCloudLevel.id)
      );
      setSelectedCloudLevelId("");
      setSavedLevelDropdownOpen(false);
      announce(`Deleted saved level "${selectedCloudLevel.level_code}".`);
    } catch (error) {
      announce(`Delete failed: ${error.message}`);
    } finally {
      setCloudBusy(false);
    }
  }

  function updateSelectedSpawnerField(fieldName, value) {
    updateActiveLevel((level) => {
      const board = readPhaseBoard(level);
      const idx = getPieceIndex(board, selectedCell.collumn, selectedCell.line);
      const piece = board.pieces[idx];

      if (fieldName === "quantity" && usesSharedThreatQuantity(piece.type)) {
        syncSharedThreatQuantity(board, piece.type, value);
        return;
      }

      piece.spawnerData[fieldName] = value;
    });
  }

  function adjustSharedThreatQuantity(pieceType, delta) {
    const minimum = getPieceMinSharedThreatQuantity(pieceType);
    const currentValue = getSharedThreatQuantity(activeBoard, pieceType) ?? minimum;
    const nextValue = Math.max(minimum, currentValue + delta);

    updateActiveLevel((level) => {
      const board = readPhaseBoard(level);
      syncSharedThreatQuantity(board, pieceType, nextValue);
    });
  }

  function isInvisibleEmptyCell(piece) {
    return piece.type === "Empty";
  }

  function cellClass(piece) {
    const classes = ["board-cell"];

    if (piece.type === "PartOf") {
      classes.push("is-part");
    } else if (piece.type !== "NoElement" && piece.type !== "Empty") {
      classes.push("is-solid");
    }

    if (piece.type === "NoElement") {
      classes.push("is-no-element");
    }

    if (isInvisibleEmptyCell(piece)) {
      classes.push("is-empty-hole");
    }

    return classes.join(" ");
  }

  function renderBoardCell(piece, options = {}) {
    const { readOnly = false, anchorFootprints = activeBoardFootprints } = options;
    const key = `${piece.collumn}:${piece.line}`;
    const isSelected = !readOnly && selectedCell.collumn === piece.collumn && selectedCell.line === piece.line;
    const centerPreviewType =
      piece.type === "PartOf"
        ? "Empty"
        : isWormType(piece.type) && Boolean(piece.startImune)
          ? HIDDEN_WORM_PROXY_TYPE
        : isAlchemyBoxType(piece.type)
          ? getAlchemySpriteType(piece.type, piece.pieceColor)
          : piece.type;
    const cellHp = isMalikFight && piece.type !== "PartOf" ? pieceHitPointsForBoardPiece(piece) : null;
    const centerPieceSize =
      anchorFootprints.get(`${piece.collumn}:${piece.line}`) ?? getPieceSize(piece.type);
    const isMultiAnchor = piece.type !== "PartOf" && (centerPieceSize.width > 1 || centerPieceSize.height > 1);
    const centerSpriteStyle = isMultiAnchor
      ? {
          width: `${
            centerPieceSize.width * BOARD_CELL_SIZE_PX +
            (centerPieceSize.width - 1) * BOARD_CELL_GAP_PX
          }px`,
          height: `${
            centerPieceSize.height * BOARD_CELL_SIZE_PX +
            (centerPieceSize.height - 1) * BOARD_CELL_GAP_PX
          }px`,
          zIndex: 4
        }
      : undefined;
    const layerSummonLines = [
      { label: "C", type: piece.type },
      { label: "BG", type: piece.backgroundType },
      { label: "FG", type: piece.foregroundType }
    ]
      .map(({ label, type }) => {
        const summonText = buildPieceSummonTooltip(type);
        if (!summonText) return "";
        return `${label} ${summonText}`;
      })
      .filter(Boolean);
    const boardCellTitle = [
      `(${piece.collumn}, ${piece.line})`,
      `BG:${piece.backgroundType}`,
      `C:${piece.type}`,
      `FG:${piece.foregroundType}`,
      ...layerSummonLines
    ].join("\n");

    return (
      <button
        key={key}
        className={`${cellClass(piece)} ${isSelected ? "is-active-cell" : ""} ${
          isMultiAnchor ? "is-multi-anchor" : ""
        } ${
          readOnly ? "is-read-only" : ""
        }`.trim()}
        onMouseDown={() => {
          if (readOnly) return;
          onCellMouseDown(piece.collumn, piece.line);
        }}
        onMouseEnter={() => {
          if (readOnly) return;
          onCellMouseEnter(piece.collumn, piece.line);
        }}
        onMouseUp={() => setPainting(false)}
        onContextMenu={(event) => {
          event.preventDefault();
          if (readOnly) return;
          setTool(TOOL.ERASE);
          applyAtCell(piece.collumn, piece.line, TOOL.ERASE);
        }}
        title={boardCellTitle}
      >
        <PieceSprite
          type={piece.backgroundType}
          layer={LAYER.BACKGROUND}
          className="layer-background"
        />
        <PieceSprite
          type={centerPreviewType}
          layer={LAYER.CENTER}
          className="layer-center"
          style={centerSpriteStyle}
        >
          {cellHp ? <span className="board-cell-hp">{"\u2764\uFE0F"} {cellHp}</span> : null}
        </PieceSprite>
        <PieceSprite
          type={piece.foregroundType}
          layer={LAYER.FOREGROUND}
          className="layer-foreground"
        />
        <span className="board-cell-coords">
          {piece.collumn},{piece.line}
        </span>
      </button>
    );
  }

  return (
    <div
      className="app-root"
      style={{
        "--left-panel-width": `${leftPanelWidth}px`,
        "--palette-width": `${paletteWidth}px`
      }}
    >
      {isCompactLayout ? (
        <div className="builder-mobile-toolbar">
          <button
            type="button"
            className="builder-mobile-toggle"
            onClick={() => setMobileLeftPanelOpen((previous) => !previous)}
            aria-label="Toggle builder menu"
          >
            <Menu size={18} aria-hidden="true" />
            <span>Menu</span>
          </button>
        </div>
      ) : null}
      {isAnyMobileDrawerOpen ? (
        <button
          type="button"
          className="builder-mobile-backdrop"
          aria-label="Close side panels"
          onClick={() => setMobileLeftPanelOpen(false)}
        />
      ) : null}
      <section className="main-shell">
        <aside
          className={`left-panel ${isCompactLayout ? "is-mobile-drawer" : ""} ${
            mobileLeftPanelOpen ? "is-mobile-open" : ""
          }`.trim()}
        >
          {isCompactLayout ? (
            <div className="builder-drawer-header">
              <strong>Builder Menu</strong>
              <button
                type="button"
                className="builder-drawer-close"
                onClick={() => setMobileLeftPanelOpen(false)}
                aria-label="Close builder menu"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <section className="builder-session">
            <div className="builder-session-row">
              <div className="builder-session-meta">
                <strong>{currentUser?.username || "builder"}</strong>
                <small>
                  Signed in
                  {showEnvironment ? ` | ${activeEnvironment.label}` : ""}
                </small>
              </div>
              {showEnvironment ? (
                <span className="environment-badge builder-environment-badge">
                  {activeEnvironment.label}
                </span>
              ) : null}
              <div className="builder-session-actions">
                <button
                  type="button"
                  className="btn-game-secondary"
                  onClick={() => {
                    setPasswordPanelOpen((previous) => !previous);
                    setPasswordError("");
                  }}
                >
                  {passwordPanelOpen ? "Cancel" : "Change Password"}
                </button>
                <button type="button" className="btn-game-secondary" onClick={() => onLogout?.()}>
                  Logout
                </button>
              </div>
            </div>
            {passwordPanelOpen ? (
              <div className="builder-session-password">
                <label>
                  Current Password
                  <input
                    type="password"
                    value={currentPasswordDraft}
                    onChange={(event) => setCurrentPasswordDraft(event.target.value)}
                    autoComplete="current-password"
                  />
                </label>
                <label>
                  New Password
                  <input
                    type="password"
                    value={newPasswordDraft}
                    onChange={(event) => setNewPasswordDraft(event.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                  />
                </label>
                <label>
                  Confirm New Password
                  <input
                    type="password"
                    value={confirmPasswordDraft}
                    onChange={(event) => setConfirmPasswordDraft(event.target.value)}
                    autoComplete="new-password"
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !passwordBusy) {
                        submitPasswordChange();
                      }
                    }}
                  />
                </label>
                {passwordError ? <p className="form-error builder-session-password-error">{passwordError}</p> : null}
                <button
                  type="button"
                  className="btn-game-primary"
                  disabled={passwordBusy}
                  onClick={submitPasswordChange}
                >
                  {passwordBusy ? "Updating..." : "Update Password"}
                </button>
              </div>
            ) : null}
          </section>

          <div className="saved-level-heading">
            <h2>My Saved Levels</h2>
            <span className="saved-level-count">
              {savedLevelCount}/{maxSavedLevels ?? "∞"}
            </span>
          </div>
          <small className="saved-level-code-note">
            {selectedCloudLevelId
              ? `Selected code: ${selectedLevelMetadata?.level_code || ""}`
              : `Next code: ${nextAutoLevelCode}`}
          </small>
          <div className="saved-level-picker" ref={savedLevelDropdownRef}>
            <button
              type="button"
              className="saved-level-picker-trigger btn-game-secondary"
              disabled={cloudBusy || cloudLoading}
              onClick={() => setSavedLevelDropdownOpen((previous) => !previous)}
            >
              {selectedSavedLevelLabel}
            </button>
            {savedLevelDropdownOpen ? (
              <div className="saved-level-dropdown">
                <input
                  type="text"
                  value={savedLevelSearch}
                  onChange={(event) => setSavedLevelSearch(event.target.value)}
                  placeholder="Search code, category or type"
                />
                <div className="saved-level-options">
                  {filteredCloudLevels.length === 0 ? (
                    <p className="saved-level-empty">No matching levels.</p>
                  ) : (
                    filteredCloudLevels.map((levelEntry) => {
                      const isSelectedCloudLevel = selectedCloudLevelId === levelEntry.id;
                      return (
                        <button
                          key={levelEntry.id}
                          type="button"
                          className={`saved-level-option ${isSelectedCloudLevel ? "is-selected" : ""}`}
                          onClick={() => {
                            void selectAndLoadCloudLevel(levelEntry);
                          }}
                        >
                          <strong>{levelEntry.level_code}</strong>
                          <small>
                            {[levelEntry.category_name, levelEntry.gameplay_type || "ThreatClear"]
                              .filter(Boolean)
                              .join(" | ")}
                          </small>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            ) : null}
          </div>
          <div className="saved-level-actions">
            <button
              className="btn-game-secondary"
              onClick={startNewCloudLevel}
              disabled={cloudBusy || isLevelLimitReached}
            >
              New Level
            </button>
            <button
              className="btn-game-primary"
              onClick={saveCloudLevelEntry}
              disabled={cloudBusy || !token || (selectedCloudLevelId && !isSelectedOwnCloudLevel)}
            >
              Save
            </button>
            <button
              className="btn-game-danger"
              onClick={deleteCloudLevelEntry}
              disabled={cloudBusy || !token || !isSelectedOwnCloudLevel}
            >
              Delete
            </button>
          </div>
          {isAdministrator ? (
            <details className="left-accordion admin-levels-accordion">
              <summary>Other User Levels (Admin)</summary>
              <div className="left-accordion-content admin-other-levels">
                <label>
                  Load Other User Level
                  <select
                    value={selectedAdminOtherLevelId}
                    onChange={(event) => setSelectedAdminOtherLevelId(event.target.value)}
                    disabled={cloudBusy || adminOtherLevelsLoading}
                  >
                    <option value="">Select creator level</option>
                    {adminOtherLevels.map((levelEntry) => (
                      <option key={levelEntry.id} value={levelEntry.id}>
                        {`${getCreatorDisplayName(levelEntry)} - ${levelEntry.display_name || levelEntry.level_code}`}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  className="btn-game-secondary"
                  onClick={loadSelectedAdminOtherLevel}
                  disabled={
                    cloudBusy ||
                    adminOtherLevelsLoading ||
                    !selectedAdminOtherLevelId
                  }
                >
                  Load
                </button>
              </div>
            </details>
          ) : null}
          {selectedCloudLevelId && !isSelectedOwnCloudLevel ? (
            <small>Loaded creator level is read-only. Press New Level to save a copy.</small>
          ) : null}
          {cloudLoading ? <small>Loading saved levels...</small> : null}
          {isAdministrator && adminOtherLevelsLoading ? (
            <small>Loading creator levels...</small>
          ) : null}

          <details className="left-accordion" open>
            <summary>Level Settings</summary>
            <div className="left-accordion-content">
              {isAdminUser ? (
                <label>
                  ID
                  <input
                    value={activeLevel.id}
                    onChange={(event) =>
                      updateActiveLevel((level) => {
                        level.id = event.target.value;
                      })
                    }
                  />
                </label>
              ) : (
                <small>Level ID is auto-set from the saved level code.</small>
              )}
              <label>
                Category (optional)
                <input
                  value={levelCategoryName}
                  maxLength={80}
                  placeholder="e.g. Welcome Town Set"
                  onChange={(event) => setLevelCategoryName(event.target.value)}
                />
              </label>
              <label>
                Max Move
                <input
                  type="number"
                  min={1}
                  value={activeLevel.maxMove}
                  onChange={(event) =>
                    updateActiveLevel((level) => {
                      level.maxMove = Number(event.target.value) || 1;
                    })
                  }
                />
              </label>
              {isAdminUser ? (
                <label>
                  Initial Wish Progress
                  <input
                    type="number"
                    step="0.1"
                    value={activeLevel.initialWishProgressValue}
                    onChange={(event) =>
                      updateActiveLevel((level) => {
                        level.initialWishProgressValue = Number(event.target.value) || 0;
                      })
                    }
                  />
                </label>
              ) : null}
              {isAdminUser ? (
                <label>
                  Gameplay Type
                  <select
                    value={activeLevel.gameplayType}
                    onChange={(event) => setGameplayType(event.target.value)}
                  >
                    {GAMEPLAY_TYPES.map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <small>Gameplay Type: {activeLevel.gameplayType || "MalikFight"}</small>
              )}
              {isAdminUser ? (
                <label>
                  Basic Pieces (comma separated)
                  <input
                    value={activeLevel.basicPieces.join(", ")}
                    onChange={(event) =>
                      updateActiveLevel((level) => {
                        level.basicPieces = sanitizeCsvPieces(event.target.value);
                      })
                    }
                  />
                </label>
              ) : null}
            </div>
          </details>

          {isAdminUser ? (
            <details className="left-accordion" open>
              <summary>Board</summary>
              <div className="left-accordion-content">
                <label>
                  Columns
                  <input
                    type="number"
                    min={1}
                    value={activeBoard.collumn}
                    disabled={isMalikFight}
                    onChange={(event) => updateBoardSize(Number(event.target.value), activeBoard.line)}
                  />
                </label>
                <label>
                  Rows
                  <input
                    type="number"
                    min={1}
                    value={activeBoard.line}
                    disabled={isMalikFight}
                    onChange={(event) => updateBoardSize(activeBoard.collumn, Number(event.target.value))}
                  />
                </label>
                {isMalikFight && (
                  <>
                    <small className="malik-fixed-note">
                      Malik board is fixed: 9 columns x 13 rows (top rows 0-4, row 5 locked, bottom rows 6-12).
                    </small>
                    <button onClick={initializeMalikLayout}>Reset Malik Layout</button>
                  </>
                )}
              </div>
            </details>
          ) : null}

          {treasureMapSharedQuantity != null ? (
            <details className="left-accordion" open>
              <summary>Treasure Maps</summary>
              <div className="left-accordion-content">
                <div className="shared-threat-control">
                  <span className="shared-threat-label">Shared Target Value</span>
                  <div className="shared-threat-stepper" aria-label="Treasure Map shared threat value">
                    <button
                      type="button"
                      onClick={() => adjustSharedThreatQuantity("TreasureMap", -1)}
                      disabled={treasureMapSharedQuantity <= getPieceMinSharedThreatQuantity("TreasureMap")}
                      aria-label="Decrease Treasure Map value"
                    >
                      -
                    </button>
                    <span>{treasureMapSharedQuantity}</span>
                    <button
                      type="button"
                      onClick={() => adjustSharedThreatQuantity("TreasureMap", 1)}
                      aria-label="Increase Treasure Map value"
                    >
                      +
                    </button>
                  </div>
                  <small>All Treasure Maps on this board share this same target value.</small>
                </div>
              </div>
            </details>
          ) : null}

          {isMalikFight ? (
            <details className="left-accordion" open>
              <summary>Malik Phases</summary>
              <div className="left-accordion-content">
                <div className="phase-row">
                  <button
                    onClick={() => setActivePhaseIndex((value) => Math.max(0, value - 1))}
                    disabled={activePhaseIndex === 0}
                  >
                    Prev
                  </button>
                  <span>{activePhaseIndex === 0 ? "Base Board" : `Phase ${activePhaseIndex}`}</span>
                  <button
                    onClick={() =>
                      setActivePhaseIndex((value) => Math.min(phaseCount - 1, value + 1))
                    }
                    disabled={activePhaseIndex >= phaseCount - 1}
                  >
                    Next
                  </button>
                </div>
                <button onClick={addPhaseFromCurrent}>Create Phase From Current</button>
                <button onClick={removeCurrentPhase} disabled={activePhaseIndex === 0}>
                  Remove Current Phase
                </button>
              </div>
            </details>
          ) : null}

          <label>
            Zoom
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.05}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
          </label>
          <small>{zoom.toFixed(1)}x</small>

          {isAdminUser ? (
            <details className="left-accordion" open>
              <summary>Cell Inspector</summary>
              <div className="left-accordion-content">
                <p>
                  ({selectedCell.collumn}, {selectedCell.line})
                </p>
                <small>Type: {selectedPieceData.type}</small>
                <label>
                  {usesSharedThreatQuantity(selectedPieceData.type)
                    ? "Shared Target Quantity"
                    : "Spawner Quantity"}
                  <input
                    type="number"
                    min={
                      usesSharedThreatQuantity(selectedPieceData.type)
                        ? getPieceMinSharedThreatQuantity(selectedPieceData.type)
                        : 0
                    }
                    value={
                      usesSharedThreatQuantity(selectedPieceData.type)
                        ? getSharedThreatQuantity(activeBoard, selectedPieceData.type) ??
                          getPieceMinSharedThreatQuantity(selectedPieceData.type)
                        : selectedPieceData.spawnerData.quantity
                    }
                    onChange={(event) =>
                      updateSelectedSpawnerField("quantity", Number(event.target.value) || 0)
                    }
                  />
                </label>
                <label>
                  Spawnable Pieces (csv)
                  <input
                    value={selectedPieceData.spawnerData.spawnablePieces.join(", ")}
                    onChange={(event) =>
                      updateSelectedSpawnerField(
                        "spawnablePieces",
                        parsePieceCsvAllowEmpty(event.target.value)
                      )
                    }
                  />
                </label>
                <label>
                  Sequential Pieces (csv)
                  <input
                    value={selectedPieceData.spawnerData.sequentialPieces.join(", ")}
                    onChange={(event) =>
                      updateSelectedSpawnerField(
                        "sequentialPieces",
                        parsePieceCsvAllowEmpty(event.target.value)
                      )
                    }
                  />
                </label>
              </div>
            </details>
          ) : null}
        </aside>
        <div
          className="column-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize left panel"
          onMouseDown={(event) => beginColumnResize("left", event)}
        />

        <main className="canvas-shell">
          <section
            className="board-scroll"
            ref={boardScrollRef}
            style={{
              "--board-scroll-bg-image": `url("${import.meta.env.BASE_URL}backgrounds/WelcomeTown.png")`
            }}
          >
            {shouldShowTargetsFloat && (
              <div
                className={`threats-float ${isMalikFight ? "is-malik" : ""}`.trim()}
                aria-label={isMalikFight ? "Malik targets" : "Threat targets"}
              >
                {isMalikFight && (
                  <article className="threat-target-card threat-target-total-hp" title="Total Malik target HP">
                    <span className="threat-target-total-label">Total Malik HP</span>
                    <span className="threat-target-total-value">❤️ {malikTargetSummary.totalHp}</span>
                  </article>
                )}
                {displayedTargets.map((target) => (
                  <article
                    key={`${target.type}-${target.isSpawned ? "spawned" : "base"}`}
                    className={`threat-target-card ${target.isSpawned ? "is-spawned-target" : ""}`.trim()}
                    title={buildTargetCardTooltip(target)}
                  >
                    {target.isSpawned ? <span className="threat-target-badge">Spawn</span> : null}
                    <span className="threat-target-preview">
                      <PieceSprite
                        type={target.type}
                        layer={getTargetLayer(target.type)}
                        className="threat-target-sprite"
                        allowEmpty
                      />
                    </span>
                    <span className="threat-target-quantity">{target.quantity}</span>
                  </article>
                ))}
              </div>
            )}
            {!isMalikFight && (
              <div
                className="board-grid"
                ref={zoomTargetRef}
                style={{
                  gridTemplateColumns: `repeat(${activeBoard.collumn}, 62px)`,
                  transform: `scale(${zoom})`
                }}
              >
                {activeBoard.pieces.map((piece) =>
                  renderBoardCell(piece, { anchorFootprints: activeBoardFootprints })
                )}
              </div>
            )}

            {isMalikFight && (
              <div className="malik-board-stack" ref={zoomTargetRef} style={{ transform: `scale(${zoom})` }}>
                <div className="malik-board-section">
                  <div
                    className="board-grid"
                    style={{ gridTemplateColumns: `repeat(${MALIK_LAYOUT.COLUMNS}, 62px)` }}
                  >
                    {malikTopPieces.map((piece) =>
                      renderBoardCell(piece, { anchorFootprints: activeBoardFootprints })
                    )}
                  </div>
                </div>

                <div className="malik-board-section">
                  <div
                    className={`board-grid ${isEditingMalikPhaseTopOnly ? "is-locked-grid" : ""}`}
                    style={{ gridTemplateColumns: `repeat(${MALIK_LAYOUT.COLUMNS}, 62px)` }}
                  >
                    {malikBottomPieces.map((piece) =>
                      renderBoardCell(piece, {
                        readOnly: isEditingMalikPhaseTopOnly,
                        anchorFootprints: displayBottomFootprints
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </main>
      </section>
      <div
        className="palette-resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize palette panel"
        onMouseDown={(event) => beginColumnResize("palette", event)}
      />

      <section className="palette-dock">
        <section className="palette-tools">
          <div className="palette-tools-header">
            <h3>Tools</h3>
          </div>
          <div className="tool-grid">
            {TOOL_OPTIONS.map(({ mode, label, Icon }) => (
              <button
                key={mode}
                type="button"
                className={`tool-button ${tool === mode ? "is-selected" : ""}`.trim()}
                onClick={() => setTool(mode)}
                title={label}
                aria-label={label}
              >
                <Icon className="tool-button-icon" aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
        {displayedLayer === LAYER.CENTER ? (
          <section className="palette-type-tabs">
            <button
              type="button"
              className={effectivePaletteContent === PALETTE_CONTENT.MALIK ? "is-selected" : ""}
              disabled={!isMalikFight}
              onClick={() => setPaletteContent(PALETTE_CONTENT.MALIK)}
            >
              Malik
            </button>
            <button
              type="button"
              className={effectivePaletteContent === PALETTE_CONTENT.BOARD ? "is-selected" : ""}
              disabled={!isMalikFight || isBoardPaletteLocked}
              onClick={() => setPaletteContent(PALETTE_CONTENT.BOARD)}
            >
              Board
            </button>
          </section>
        ) : null}
        <section className="palette-layer-tabs">
          {visibleLayers.map((layer) => (
            <button
              key={layer}
              className={displayedLayer === layer ? "is-selected" : ""}
              onClick={() => {
                setActiveLayer(layer);
                if (!getPaletteForLayer(layer).includes(selectedPiece)) {
                  setSelectedPiece(getPaletteForLayer(layer)[0] || "Empty");
                }
              }}
            >
              {layer}
            </button>
          ))}
        </section>
        <div className="palette-grid">
          {palette.map((pieceType) => {
            const label = pieceLabel(pieceType) || "Empty";
            const hp = pieceHitPoints(pieceType);
            const spawnStats = getPieceSpawnStats(pieceType);
            const hasSpawnStats = spawnStats.totalCount > 0 && spawnStats.iconType !== "Empty";
            const hasCooldownStats = typeof spawnStats.cooldownLabel === "string" && spawnStats.cooldownLabel.length > 0;
            const spawnIconLabel = hasSpawnStats
              ? pieceLabel(spawnStats.iconType) || spawnStats.iconType
              : "";
            const previewType = isAlchemyBoxType(pieceType)
              ? getAlchemySpriteType(pieceType, selectedAlchemyColor)
              : pieceType;
            return (
              <button
                key={pieceType}
                className={selectedPiece === pieceType ? "palette-piece is-selected" : "palette-piece"}
                title={buildPieceHoverTooltip(pieceType, [
                  hp ? `\u2764\uFE0F ${hp}` : "",
                  hasSpawnStats
                    ? `Summons: ${spawnStats.totalCount}${spawnIconLabel ? ` (${spawnIconLabel})` : ""}`
                    : "",
                  hasCooldownStats ? `Cooldown: ${spawnStats.cooldownLabel} turn(s)` : ""
                ])}
                onClick={() => {
                  const alchemyPaletteSelection = parseAlchemyPaletteType(pieceType);
                  if (alchemyPaletteSelection) {
                    setSelectedAlchemyColor(alchemyPaletteSelection.pieceColor);
                  }
                  setSelectedPiece(pieceType);
                  if (tool === TOOL.FILL) setTool(TOOL.PAINT);
                }}
              >
                <span className="palette-piece-preview">
                  <PieceSprite
                    type={previewType}
                    layer={displayedLayer}
                    className="palette-piece-sprite"
                    allowEmpty
                  />
                </span>
                <span className="palette-piece-meta">
                  <span className="palette-piece-name">{label}</span>
                  {hp || hasSpawnStats || hasCooldownStats ? (
                    <span className="palette-piece-stats">
                      {hp ? <span className="palette-piece-hp">❤️ {hp}</span> : null}
                      {hasSpawnStats ? (
                        <span className="palette-piece-spawn">
                          <span className="palette-piece-spawn-icon-wrap">
                            <PieceSprite
                              type={spawnStats.iconType}
                              layer={LAYER.CENTER}
                              className="palette-piece-spawn-icon"
                              allowEmpty
                            />
                          </span>
                          <span className="palette-piece-spawn-count">{spawnStats.totalCount}</span>
                        </span>
                      ) : null}
                      {hasCooldownStats ? (
                        <span className="palette-piece-cooldown">⏱ {spawnStats.cooldownLabel}</span>
                      ) : null}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {toastMessage ? (
        <div className="builder-toast" role="status" aria-live="polite">
          {toastMessage}
        </div>
      ) : null}

    </div>
  );
}

export default App;

